import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../Styles/Admin/assessment/ReportPage.css";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import API_BASE_URL from "../config";

// Admin's full Employee Report page (its own data fetching, its own
// table, its own edit/delete modals). Real file:
// client/src/pages/admin/assessment/EmployeeReport.js
// CustomerReportPage.js lives in client/src/components/, so we go up
// one level out of components/ and into pages/.
import EmployeeReports from "../pages/admin/assessment/EmployeeReport";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const CHART_FONT_FAMILY = "'Inter', 'Segoe UI', -apple-system, Arial, sans-serif";
ChartJS.defaults.font.family = CHART_FONT_FAMILY;
ChartJS.defaults.font.size = 12;
ChartJS.defaults.color = "#000000";

const CHART_STYLE_DEFAULTS = {
  chartFontFamily: CHART_FONT_FAMILY,

  // "Gender Comparison" bar chart
  genderChartXFontSize: 13,
  genderChartXColor: "#222222",
  genderChartYFontSize: 14,
  genderChartYColor: "#222222",
  genderDatalabelFontSize: 11,
  genderDatalabelColor: "#000000",
  genderLegendFontSize: 18,
  genderLegendColor: "#000000",
  genderChartXFontWeight: "400",
  genderChartYFontWeight: "500",
  genderDatalabelFontWeight: "700",
  genderLegendFontWeight: "500",
};

// Reads the --gender-chart-* / --chart-font-family custom properties off
// .analytics-page and turns them into the plain numbers/strings Chart.js
// options expect. Runs once on mount (canvas text isn't reactive like DOM
// text). If a variable isn't set, CHART_STYLE_DEFAULTS is used.
function useChartCSSVars() {
  const [vars, setVars] = useState(CHART_STYLE_DEFAULTS);

  useEffect(() => {
    const pageEl = document.querySelector(".analytics-page");
    if (!pageEl) return;
    const computed = getComputedStyle(pageEl);

    const readNum = (name, fallback) => {
      const raw = computed.getPropertyValue(name).trim();
      const parsed = parseFloat(raw);
      return isNaN(parsed) ? fallback : parsed;
    };
    const readStr = (name, fallback) => {
      const raw = computed.getPropertyValue(name).trim();
      return raw || fallback;
    };

    setVars({
      chartFontFamily: readStr("--chart-font-family", CHART_STYLE_DEFAULTS.chartFontFamily),

      genderChartXFontSize: readNum("--gender-chart-x-font-size", CHART_STYLE_DEFAULTS.genderChartXFontSize),
      genderChartXColor: readStr("--gender-chart-x-color", CHART_STYLE_DEFAULTS.genderChartXColor),
      genderChartYFontSize: readNum("--gender-chart-y-font-size", CHART_STYLE_DEFAULTS.genderChartYFontSize),
      genderChartYColor: readStr("--gender-chart-y-color", CHART_STYLE_DEFAULTS.genderChartYColor),
      genderDatalabelFontSize: readNum("--gender-chart-datalabel-font-size", CHART_STYLE_DEFAULTS.genderDatalabelFontSize),
      genderDatalabelColor: readStr("--gender-chart-datalabel-color", CHART_STYLE_DEFAULTS.genderDatalabelColor),
      genderLegendFontSize: readNum("--gender-chart-legend-font-size", CHART_STYLE_DEFAULTS.genderLegendFontSize),
      genderLegendColor: readStr("--gender-chart-legend-color", CHART_STYLE_DEFAULTS.genderLegendColor),
      genderChartXFontWeight: readStr("--gender-chart-x-font-weight", CHART_STYLE_DEFAULTS.genderChartXFontWeight),
      genderChartYFontWeight: readStr("--gender-chart-y-font-weight", CHART_STYLE_DEFAULTS.genderChartYFontWeight),
      genderDatalabelFontWeight: readStr("--gender-chart-datalabel-font-weight", CHART_STYLE_DEFAULTS.genderDatalabelFontWeight),
      genderLegendFontWeight: readStr("--gender-chart-legend-font-weight", CHART_STYLE_DEFAULTS.genderLegendFontWeight),
    });
  }, []);

  return vars;
}

/* ------------------------------------------------------------------
   Icons — same asset paths as the admin analytics page.
   ------------------------------------------------------------------ */
const ICONS = {
  calendar: "/ana_formkit_date.png",
  company: "/ana_all_companies_icons.png",
  search: "/ana_search_icons.png",
  chevronDown: "/ana_arrow_down.png",
  uploadPdf: "/ana_tabler_upload.png",
  pdf: "/icons/pdf.png",
  excel: "/icons/excel.png",
  totalEmployees: "/ana_employee-icons.png",
  totalResponses: "/ana_response_icons.png",
  totalUnResponses: "/ana_unresponse_icons.png",
  medal: "/ana_certificate_icons.png",
  star: "/ana_guidance_star.png",
  emojiPositive: "/ana_green_emoji.png",
  emojiNeutral: "/ana_yellow_emoji.png",
  emojiNegative: "/ana_red_emoji.png",
  // Overall Score "Interpretation" banner — separate icon per state.
  interpretationAbove: "/ana_interpretation_above.png",
  interpretationBelow: "/ana_interpretation_below.png",

  // Card header icons (navy #02133F header bar, left of the title).
  headOverall: "/ana_head_overall.png",
  headFramework: "/ana_head_framework.png",
  headDemographic: "/ana_head_demographic.png",
  headDepartments: "/ana_head_departments.png",
  headExperience: "/ana_head_experience.png",
  headGender: "/ana_head_gender.png",
  headFeedback: "/ana_head_feedback.png",
  headStatements: "/ana_head_statements.png",

  // Demographic section: dropdown icons + the two green insight notes
  departmentFilter: "/ana_department_filter.png",
  experienceFilter: "/ana_experience_filter.png",
  noteDepartment: "/ana_note_department.png",
  noteExperience: "/ana_note_experience.png",

  // Statements section: "Key Insights" box icon
  keyInsights: "/ana_key_insights.png",
};

// Inline SVG icons (one per framework) so no extra image files are needed.
const svgIcon = (inner, color) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`
  )}`;

const FRAMEWORK_ICON_SET = {
  // 1. Leadership, Trust & Integrity: shield with a tick
  leadership: {
    bg: "#DCEFFB",
    icon: svgIcon('<path d="M12 3l7 3v5c0 4.5-3 8.2-7 10-4-1.8-7-5.5-7-10V6l7-3z"/><path d="M9 12l2 2 4-4"/>', "#2B7FD4"),
  },
  // 2. Employee Experience, Belonging & Collaboration: group of people
  experience: {
    bg: "#DDF6E8",
    icon: svgIcon('<circle cx="9" cy="8" r="3"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><circle cx="17" cy="9" r="2.5"/><path d="M17 14c2.8 0 5 2.2 5 5"/>', "#1F9D55"),
  },
  // 3. Wellbeing, Equity & Fair Opportunity: balance scale
  wellbeing: {
    bg: "#F1E7FB",
    icon: svgIcon('<path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10"/><path d="M12 3v18"/><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/>', "#9B59D9"),
  },
  // 4. Purpose, Meaningful Work & Achievement: target
  purpose: {
    bg: "#DDEBFB",
    icon: svgIcon('<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/>', "#2563EB"),
  },
  // 5. Learning, Innovation & Future Readiness: light bulb
  learning: {
    bg: "#FBEADD",
    icon: svgIcon('<path d="M9 18h6"/><path d="M10 21h4"/><path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z"/>', "#F2994A"),
  },
  // 6. Workplace Excellence & Sustainable Impact: trophy
  workplace: {
    bg: "#D8F5EA",
    icon: svgIcon('<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>', "#0E9F6E"),
  },
};

const defaultFrameworkStyle = { bg: "#E6EDFA", icon: "/ana_guidance_star.png" };

// Matches by keyword, so small wording changes (or the old framework
// names) still get the right icon.
function getFrameworkStyle(label) {
  const l = String(label || "").toLowerCase();
  if (/leadership|trust/.test(l)) return FRAMEWORK_ICON_SET.leadership;
  if (/experience|belonging|collaboration/.test(l)) return FRAMEWORK_ICON_SET.experience;
  if (/well-?being|inclusion|equity|fair/.test(l)) return FRAMEWORK_ICON_SET.wellbeing;
  if (/purpose|meaningful|achievement/.test(l)) return FRAMEWORK_ICON_SET.purpose;
  if (/learning|innovation|readiness/.test(l)) return FRAMEWORK_ICON_SET.learning;
  if (/workplace|sustain|impact/.test(l)) return FRAMEWORK_ICON_SET.workplace;
  return defaultFrameworkStyle;
}

// Fallback department list — only used if the report payload doesn't
// tell us which departments it has data for.
const FALLBACK_DEPARTMENTS = [
  "Business Development",
  "Finance & Accounting",
  "Human Resources (HR)",
  "Operations",
  "Sales & Marketing",
  "Information Technology",
  "Others",
];

const BENCHMARK = 70;

// Bar colours for the Departments chart (by position), as in the Figma.
const DEPT_BAR_COLORS = ["#1E90FF", "#6E7BF2", "#2DB34A", "#2563EB", "#FFB114"];
const AXIS_LINE = "#9AA1B5";

// Analytics unlocks when (completed assessments / total employees) reaches
// this percentage. Worked out per year, and for All Years combined.
// Example: a year with 10 employees needs 6 completed (60%).
const ANALYTICS_MIN_RESPONSE_RATE = 60;
const getResponseRate = (attended, total) => (total > 0 ? (attended / total) * 100 : 0);

// Default sample feedback — shown only when the backend sends none.
const MOCK_FEEDBACK = [
  { type: "positive", text: "Great leadership and supportive work culture. Plenty of learning opportunities." },
  { type: "positive", text: "My team collaborates really well and I feel heard when I raise ideas." },
  { type: "neutral", text: "Work environment is good but promotion opportunities are limited." },
  { type: "neutral", text: "This organization promotes work-life balance, but it's not always consistent." },
  { type: "negative", text: "Workload is high and feedback is not always considered." },
  { type: "negative", text: "Communication between departments could be improved." },
];

// Value used by the year dropdown for the combined "All Years" view.
const ALL_YEARS = "all";
const YEAR_FILTER_KEY = "customer-analytics-year-filter";
const ACTIVE_VIEW_KEY = "customer-active-view";

const groupBySection = (arr = []) => {
  const grouped = {};
  arr.forEach((q) => {
    if (!grouped[q.section]) grouped[q.section] = [];
    grouped[q.section].push(q);
  });
  return grouped;
};

function getRemarks(positive) {
  if (positive >= 80) return "Strong";
  if (positive >= 70) return "Good";
  return "Developing";
}

function getCertificationCategory(score) {
  if (score >= 90) return "Exceptional Outstanding Workplaces";
  if (score >= 80) return "Strong Outstanding Workplaces";
  if (score >= 70) return "Certified Outstanding Workplaces";
  return "Not Certified";
}

function getCertificationShortLabel(score) {
  if (score >= 90) return "Exceptional";
  if (score >= 80) return "Strong";
  if (score >= 70) return "Certified";
  return "Not Certified";
}

// Decode a JWT payload without any extra dependency.
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (err) {
    return null;
  }
}

// Wraps a label into short lines (by word) so narrow chart columns
// don't let adjacent category labels visually run into each other.
function wrapChartLabel(label, maxCharsPerLine = 11) {
  const words = (label || "").split(" ");
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxCharsPerLine && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  });
  if (current) lines.push(current);
  return lines;
}

/* ------------------------------------------------------------------
   mergeReports — builds the "All Years" report.
   Scores are averaged WEIGHTED BY NUMBER OF RESPONSES, so a year with
   0 responses counts for nothing. Totals (employees / responses /
   un-responses) are summed. Reports must be sorted newest first.
   ------------------------------------------------------------------ */
function mergeReports(reports) {
  const active = reports.filter((r) => (r.surveyAttended || 0) > 0);
  const latest = reports[0];

  const totals = {
    totalEmployees: reports.reduce((s, r) => s + (r.totalEmployees ?? r.employees_count ?? 0), 0),
    surveyAttended: reports.reduce((s, r) => s + (r.surveyAttended || 0), 0),
    surveyUnattended: reports.reduce((s, r) => s + (r.surveyUnattended || 0), 0),
  };

  // Nobody answered in any year: show empty
  if (!active.length) return { ...latest, ...totals };

  const wavg = (pairs) => {
    // pairs = [[value, weight], ...]
    const w = pairs.reduce((s, [, wt]) => s + wt, 0);
    return w ? Math.round(pairs.reduce((s, [v, wt]) => s + (Number(v) || 0) * wt, 0) / w) : 0;
  };
  const W = (r) => r.surveyAttended || 0;

  // Merge array items by a key, averaging the given numeric fields
  const mergeList = (getList, keyFn, fields) => {
    const map = {};
    active.forEach((r) =>
      (getList(r) || []).forEach((item) => {
        const k = keyFn(item);
        if (!map[k]) map[k] = { base: item, rows: [] };
        map[k].rows.push([item, W(r)]);
      })
    );
    return Object.values(map).map(({ base, rows }) => {
      const out = { ...base };
      fields.forEach((f) => {
        out[f] = wavg(rows.map(([it, w]) => [it[f], w]));
      });
      return out;
    });
  };

  const sectionScores = mergeList((r) => r.sectionScores, (s) => s.label, ["positive", "neutral", "negative"]);

  const departmentScores = {};
  const deptNames = new Set(active.flatMap((r) => Object.keys(r.departmentScores || {})));
  deptNames.forEach((d) => {
    departmentScores[d] = mergeList((r) => r.departmentScores?.[d], (s) => s.label, ["positive"]);
  });

  const experienceScores = mergeList((r) => r.experienceScores, (e) => e.label, ["value"]);

  const genderMap = {};
  active.forEach((r) =>
    (r.genderScores || []).forEach((g) => {
      if (!genderMap[g.label]) genderMap[g.label] = [];
      genderMap[g.label].push([g, W(r)]);
    })
  );
  const genderScores = Object.entries(genderMap).map(([label, rows]) => ({
    label,
    male: { positive: wavg(rows.map(([g, w]) => [g.male?.positive, w])) },
    female: { positive: wavg(rows.map(([g, w]) => [g.female?.positive, w])) },
  }));

  const questionsTable = mergeList(
    (r) => r.questionsTable,
    (q) => `${q.section}||${q.question}`,
    ["positive", "neutral", "negative"]
  );

  const section70Totals = {};
  const secNames = new Set(active.flatMap((r) => Object.keys(r.section70Totals || {})));
  secNames.forEach((s) => {
    section70Totals[s] = wavg(active.map((r) => [r.section70Totals?.[s], W(r)]));
  });

  const sentimentAnalysis = active.reduce(
    (acc, r) => ({
      positive: acc.positive + (r.sentimentAnalysis?.positive || 0),
      neutral: acc.neutral + (r.sentimentAnalysis?.neutral || 0),
      negative: acc.negative + (r.sentimentAnalysis?.negative || 0),
    }),
    { positive: 0, neutral: 0, negative: 0 }
  );

  return {
    ...active[0],
    ...totals,
    surveyScore: wavg(active.map((r) => [r.surveyScore, W(r)])),
    finalScore: wavg(active.map((r) => [r.finalScore, W(r)])),
    __selfEval: wavg(active.map((r) => [r.__selfEval, W(r)])),
    sectionScores,
    departmentScores,
    experienceScores,
    genderScores,
    questionsTable,
    section70Totals,
    sentimentAnalysis,
    sampleFeedback: active.flatMap((r) => r.sampleFeedback || []).slice(0, 6),
  };
}

/* ------------------------------------------------------------------
   Dropdown keyboard support (same contract as the admin page).

     Closed : Enter / Space / ArrowDown / ArrowUp opens the list.
              Typing a letter opens it too.
     Open   : ArrowUp / ArrowDown move the highlight (wraps around)
              Home / End = first / last
              Enter or Space picks the highlighted option
              Escape closes and returns focus to the box
              Tab closes and moves on to the next control
     Any    : ArrowLeft / ArrowRight jumps to the previous / next dropdown;
              the next one opens if this one was open
   ------------------------------------------------------------------ */
let dropdownUidCounter = 0;
function useDropdownUid(prefix) {
  const ref = useRef(null);
  if (ref.current === null) {
    dropdownUidCounter += 1;
    ref.current = `${prefix}-${dropdownUidCounter}`;
  }
  return ref.current;
}

function isPrintableKey(e) {
  return e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
}

// After a selection the box goes back to its normal (un-highlighted) look
// but keeps keyboard focus, so Left / Right / Tab still work from it.
// (CSS: [data-quiet="1"] on the box's wrapper hides the ring.)
function setQuietFocus(triggerEl, quiet) {
  const wrap = triggerEl && triggerEl.parentElement;
  if (!wrap) return;
  if (quiet) wrap.setAttribute("data-quiet", "1");
  else wrap.removeAttribute("data-quiet");
}

// Left / Right arrow = jump to the previous / next dropdown on the page.
function handleDropdownSideKey(e, triggerEl, isOpen) {
  setQuietFocus(triggerEl, false);
  if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return false;
  if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return false;
  const dir = e.key === "ArrowRight" ? 1 : -1;
  const el = e.target;
  if (el && el.tagName === "INPUT") {
    if (el.selectionStart !== el.selectionEnd) return false;
    if (dir === 1 && el.selectionStart !== el.value.length) return false;
    if (dir === -1 && el.selectionStart !== 0) return false;
  }
  const all = Array.from(document.querySelectorAll("[data-dd-nav]")).filter(
    (node) => !node.disabled && node.getAttribute("aria-disabled") !== "true" && node.offsetParent !== null
  );
  const idx = all.indexOf(triggerEl);
  const next = idx >= 0 ? all[idx + dir] : null;
  if (!next) return false;
  e.preventDefault();
  next.focus();
  if (isOpen) next.click();
  return true;
}

function useDropdownNav({ open, labels, onPick, typeAhead = false, spaceSelects = false, homeEnd = true }) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);
  const scrollRef = useRef(true);
  const typeState = useRef({ text: "", timer: null }).current;

  // scroll=false is used for mouse hover so the list never jumps under the pointer
  const activate = (index, scroll = true) => {
    scrollRef.current = scroll;
    setActiveIndex(index);
  };

  useEffect(() => {
    if (!open || activeIndex < 0 || !scrollRef.current || !listRef.current) return;
    const list = listRef.current;
    const el = list.querySelector(`[data-nav-index="${activeIndex}"]`);
    if (!el) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = Math.max(0, top - 6);
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 6;
  }, [open, activeIndex]);

  useEffect(() => () => clearTimeout(typeState.timer), [typeState]);

  // Returns true when the key was consumed.
  const handleNavKey = (e) => {
    const count = labels.length;
    if (e.ctrlKey || e.metaKey || e.altKey) return false;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (count) activate((activeIndex + 1) % count);
        return true;
      case "ArrowUp":
        e.preventDefault();
        if (count) activate(activeIndex <= 0 ? count - 1 : activeIndex - 1);
        return true;
      case "Home":
        if (!homeEnd) return false;
        e.preventDefault();
        if (count) activate(0);
        return true;
      case "End":
        if (!homeEnd) return false;
        e.preventDefault();
        if (count) activate(count - 1);
        return true;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < count) onPick(activeIndex);
        return true;
      default: {
        if (e.key.length !== 1) return false;
        if (e.key === " " && !typeState.text) {
          if (!spaceSelects) return false;
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < count) onPick(activeIndex);
          return true;
        }
        if (!typeAhead) return false;
        e.preventDefault();
        clearTimeout(typeState.timer);
        typeState.text += e.key.toLowerCase();
        typeState.timer = setTimeout(() => {
          typeState.text = "";
        }, 700);
        const match = labels.findIndex((l) => String(l).toLowerCase().startsWith(typeState.text));
        if (match >= 0) activate(match);
        return true;
      }
    }
  };

  return { activeIndex, activate, listRef, handleNavKey };
}

/* ------------------------------------------------------------------
   FilterDropdown  (Year / Department / Experience) — button trigger + listbox
   ------------------------------------------------------------------ */
function FilterDropdown({ icon, label, placeholder, value, options, onChange, minWidth }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const listId = useDropdownUid("analytics-filter-list");

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;
  const selectedLabel = selected ? selected.label : placeholder;

  const focusTrigger = () => {
    if (triggerRef.current) triggerRef.current.focus();
  };

  const pick = (index) => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    setOpen(false);
    focusTrigger();
    setQuietFocus(triggerRef.current, true);
  };

  const nav = useDropdownNav({
    open,
    labels: options.map((o) => o.label),
    onPick: pick,
    typeAhead: true,
    spaceSelects: true,
  });

  const openList = () => {
    nav.activate(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
    focusTrigger(); // Safari doesn't focus buttons on click
  };

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleKeyDown = (e) => {
    if (handleDropdownSideKey(e, triggerRef.current, open)) return;
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openList();
      } else if (isPrintableKey(e)) {
        e.preventDefault();
        openList();
        nav.handleNavKey(e);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      return;
    }
    if (e.key === "Tab") {
      setOpen(false);
      return;
    }
    nav.handleNavKey(e);
  };

  // Firefox fires the button "click" on Space keyup — stop that double toggle.
  const handleKeyUp = (e) => {
    if (e.key === " ") e.preventDefault();
  };

  const handleBlur = (e) => {
    setQuietFocus(triggerRef.current, false);
    if (ref.current && !ref.current.contains(e.relatedTarget)) setOpen(false);
  };

  return (
    <div
      className={`analytics-dropdown${open ? " open" : ""}`}
      style={minWidth ? { minWidth } : undefined}
      ref={ref}
      onBlur={handleBlur}
    >
      <button
        type="button"
        ref={triggerRef}
        data-dd-nav="true"
        className="analytics-dropdown-trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${label}: ${selectedLabel}`}
        aria-activedescendant={open && nav.activeIndex >= 0 ? `${listId}-opt-${nav.activeIndex}` : undefined}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
      >
        {icon && <img src={icon} alt="" className="analytics-dropdown-icon" />}
        <span className={selected ? "" : "analytics-dropdown-placeholder"}>{selectedLabel}</span>
        <img src={ICONS.chevronDown} alt="" className="analytics-dropdown-chevron" />
      </button>
      {open && (
        <ul
          className="analytics-dropdown-menu"
          id={listId}
          role="listbox"
          ref={nav.listRef}
          onMouseDown={(e) => e.preventDefault()}
        >
          {options.map((o, i) => (
            <li key={`${o.value}-${i}`} role="presentation">
              <button
                type="button"
                tabIndex={-1}
                id={`${listId}-opt-${i}`}
                role="option"
                aria-selected={o.value === value}
                data-nav-index={i}
                className={`analytics-dropdown-option${o.value === value ? " selected" : ""}${
                  i === nav.activeIndex ? " active" : ""
                }`}
                onMouseEnter={() => nav.activate(i, false)}
                onClick={() => pick(i)}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Analytics PDF builder — draws the whole report with vector graphics
// (text, bars, rings, stars) instead of pasting screenshots of the page cards,
// so everything stays razor sharp and snaps to one grid.
// ---------------------------------------------------------------------------
// dd-mm-yyyy (used on every downloaded file)
const formatDownloadDate = (dt = new Date()) =>
  `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;

function drawAnalyticsPdf(pdf, d) {
  const PW = pdf.internal.pageSize.getWidth();
  const PH = pdf.internal.pageSize.getHeight();
  const M = 36;
  const CW = PW - M * 2;
  const GAP = 18;
  const FOOT = 34;
  const NAVY = "#00123F";
  const INK = "#111827";
  const MUTED = "#5B6577";
  const HAIR = "#DDE2EE";
  const GRID = "#E6E9F2";

  const rgb = (hex) => {
    const h = String(hex).replace("#", "");
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  };
  const fill = (hex) => pdf.setFillColor(...rgb(hex));
  const stroke = (hex) => pdf.setDrawColor(...rgb(hex));
  const ink = (hex) => pdf.setTextColor(...rgb(hex));
  const font = (size, bold = false) => {
    pdf.setFont("helvetica", bold ? "bold" : "normal");
    pdf.setFontSize(size);
  };
  const text = (str, x, y, opts) => pdf.text(String(str), x, y, opts);
  const wrap = (str, w, size, bold = false) => {
    font(size, bold);
    return pdf.splitTextToSize(String(str), w);
  };

  const poly = (pts, style = "F") => {
    const d2 = pts.slice(1).map((p, i) => [p[0] - pts[i][0], p[1] - pts[i][1]]);
    pdf.lines(d2, pts[0][0], pts[0][1], [1, 1], style, true);
  };
  const arcPts = (cx, cy, r, a0, a1, step = 2) => {
    const pts = [];
    const dir = a1 >= a0 ? 1 : -1;
    for (let a = a0; dir > 0 ? a < a1 : a > a1; a += dir * step) {
      const t = (a * Math.PI) / 180;
      pts.push([cx + r * Math.cos(t), cy + r * Math.sin(t)]);
    }
    const t = (a1 * Math.PI) / 180;
    pts.push([cx + r * Math.cos(t), cy + r * Math.sin(t)]);
    return pts;
  };
  const ringSeg = (cx, cy, ro, ri, a0, a1, color) => {
    if (a1 - a0 <= 0.01) return;
    fill(color);
    poly([...arcPts(cx, cy, ro, a0, a1), ...arcPts(cx, cy, ri, a1, a0)], "F");
  };
  const star = (cx, cy, r) => {
    const pts = [];
    for (let i = 0; i < 10; i += 1) {
      const rad = i % 2 === 0 ? r : r * 0.42;
      const a = (-90 + i * 36) * (Math.PI / 180);
      pts.push([cx + rad * Math.cos(a), cy + rad * Math.sin(a)]);
    }
    return pts;
  };
  // bar flat on the left, fully rounded on the right (cylinder end)
  const pillBar = (x, y, w, h, color) => {
    if (w <= 0.2) return;
    fill(color);
    const r = h / 2;
    if (w <= h) {
      pdf.roundedRect(x, y, w, h, Math.min(r, w / 2), r, "F");
      return;
    }
    pdf.rect(x, y, w - r, h, "F"); // flat left end
    pdf.circle(x + w - r, y + r, r, "F"); // rounded right end
  };

  // Draws one of the page's real icon images (data URL). Returns false when
  // the icon is missing so the caller can draw a simple fallback shape.
  const icon = (key, x, yy, w, h) => {
    const u = d.icons && d.icons[key];
    if (!u) return false;
    try {
      pdf.addImage(u, "PNG", x, yy, w, h);
      return true;
    } catch (e) {
      return false;
    }
  };

  let y = 0;
  let pageNo = 1;

  /* ---------- page furniture ---------- */
  const firstHeader = () => {
    fill(NAVY);
    pdf.rect(0, 0, PW, 70, "F");
    font(21, true);
    ink("#FFFFFF");
    text("Analytics Report", M, 34);
    font(10.5);
    ink("#C9D4F5");
    const co = wrap(d.companyName || "", CW - 140, 10.5)[0] || "";
    text(co + (d.year ? `  (${d.year})` : ""), M, 53);
    font(9);
    text(`Generated ${d.generated}`, PW - M, 53, { align: "right" });
    fill("#2656DC");
    pdf.rect(0, 70, PW, 3, "F");
    y = 92;
  };
  const runningHeader = () => {
    font(9, true);
    ink(NAVY);
    text("Analytics Report", M, 34);
    font(9);
    ink(MUTED);
    text(d.companyName || "", PW - M, 34, { align: "right" });
    stroke(NAVY);
    pdf.setLineWidth(1.2);
    pdf.line(M, 42, PW - M, 42);
    y = 62;
  };
  const newPage = () => {
    pdf.addPage();
    pageNo += 1;
    runningHeader();
  };
  const ensure = (h) => {
    if (y + h > PH - FOOT) newPage();
  };

  const section = (x, w, title, iconKey) => {
    let tx = x + 10;
    if (iconKey && d.icons && d.icons[iconKey]) {
      fill("#223668");
      pdf.roundedRect(x, y - 13, 20, 20, 5, 5, "F");
      icon(iconKey, x + 4, y - 9, 12, 12);
      tx = x + 28;
    } else {
      fill("#2656DC");
      pdf.roundedRect(x, y - 10, 3.2, 14, 1.4, 1.4, "F");
    }
    font(12, true);
    ink(NAVY);
    text(title, tx, y + 1);
    stroke(HAIR);
    pdf.setLineWidth(0.7);
    pdf.line(x, y + 8, x + w, y + 8);
    return y + 28;
  };

  /* ================= PAGE 1 ================= */
  firstHeader();

  /* ---------- KPI strip ---------- */
  {
    const h = 54;
    stroke(HAIR);
    pdf.setLineWidth(0.7);
    pdf.line(M, y, M + CW, y);
    pdf.line(M, y + h, M + CW, y + h);
    // column widths follow the content so long labels never touch a divider
    const need = d.kpis.map((k) => {
      font(k.big ? 17 : 19, true);
      const vw = pdf.getTextWidth(String(k.value));
      font(8.5);
      const lw = pdf.getTextWidth(String(k.label));
      return 54 + Math.max(vw, lw) + 16;
    });
    const needSum = need.reduce((a, b) => a + b, 0);
    const widths = need.map((n) => (n / needSum) * CW);
    let cursor = M;
    d.kpis.forEach((k, i) => {
      const x = cursor;
      cursor += widths[i];
      if (i > 0) pdf.line(x, y + 9, x, y + h - 9);
      fill(k.tint);
      pdf.roundedRect(x + 14, y + 12, 30, 30, 7, 7, "F");
      if (!icon(k.icon, x + 21, y + 19, 16, 16)) {
        fill(k.color);
        pdf.circle(x + 29, y + 27, 5, "F");
        fill(k.tint);
        pdf.circle(x + 29, y + 27, 2, "F");
      }
      font(k.big ? 17 : 19, true);
      ink(k.valueColor || INK);
      text(k.value, x + 54, y + 27);
      font(8.5);
      ink(MUTED);
      text(k.label, x + 54, y + 40);
    });
    y += h + GAP + 8;
  }

  /* ---------- Overall Score + Score by Framework ---------- */
  {
    const leftW = 180;
    const rightW = CW - leftW - GAP;
    const rx = M + leftW + GAP;
    const top = y;

    // ----- left: gauge -----
    let ly = section(M, leftW, "Overall Score", "headOverall");
    const cx = M + leftW / 2;
    const ro = 52;
    const th = 15;
    const ri = ro - th;
    const cy = ly + ro + 4;
    const A0 = 150;
    const SWEEP = 240;
    const capAt = (a, color) => {
      const rm = (ro + ri) / 2;
      const t = (a * Math.PI) / 180;
      fill(color);
      pdf.circle(cx + rm * Math.cos(t), cy + rm * Math.sin(t), th / 2, "F");
    };
    ringSeg(cx, cy, ro, ri, A0, A0 + SWEEP, d.overall.track);
    capAt(A0, d.overall.track);
    capAt(A0 + SWEEP, d.overall.track);
    const filledA = A0 + (SWEEP * Math.max(0, Math.min(100, d.overall.score))) / 100;
    if (d.overall.score > 0) {
      ringSeg(cx, cy, ro, ri, A0, filledA, d.overall.color);
      capAt(A0, d.overall.color);
      capAt(filledA, d.overall.color);
    }
    font(24, true);
    ink(d.overall.color);
    text(`${d.overall.score}%`, cx, cy + 4, { align: "center" });
    font(12.5, true);
    ink(INK);
    text(d.overall.label, cx, cy + ro + 9, { align: "center" });
    ly = cy + ro + 24;

    const chip = (label, value) => {
      fill("#E4E9FB");
      pdf.roundedRect(M, ly, leftW, 22, 5, 5, "F");
      font(8.5, true);
      ink("#2656DC");
      text(label, M + 10, ly + 14);
      ink(INK);
      text(String(value), M + leftW - 10, ly + 14, { align: "right" });
      ly += 28;
    };
    chip("70% Employee Survey", d.overall.survey);
    chip("30% Auditing Evaluation", d.overall.audit);

    const interp = wrap(d.overall.interpretation, leftW - 50, 8);
    const ih = 28 + interp.length * 10.5;
    fill(d.overall.above ? "#E3F4E8" : "#FFF1D6");
    pdf.roundedRect(M, ly, leftW, ih, 6, 6, "F");
    fill(d.overall.above ? "#00A631" : "#FCAF1B");
    pdf.roundedRect(M, ly, 4, ih, 2, 2, "F");
    font(9, true);
    ink(d.overall.above ? "#0B6B2A" : "#9A5B00");
    const hasIc = icon(d.overall.above ? "interpretationAbove" : "interpretationBelow", M + 12, ly + 8, 18, 18);
    const ix = hasIc ? M + 38 : M + 14;
    text("Interpretation", ix, ly + 15);
    font(8);
    ink(d.overall.above ? "#146B35" : "#8A5600");
    interp.forEach((ln, i) => text(ln, ix, ly + 27 + i * 10.5));
    const leftEnd = ly + ih;

    // ----- right: framework table -----
    y = top;
    let ry = section(rx, rightW, "Score by Framework (Category-Wise)", "headFramework");
    const hdrH = 22;
    fill("#EEF2FC");
    pdf.rect(rx, ry, rightW, hdrH, "F");
    font(8.5, true);
    ink(NAVY);
    const cF = rx + 8;
    const cBar = rx + 132;
    const cBen = rx + 206;
    const cStar = rx + 258;
    font(8, true);
    text("Framework", cF, ry + 14);
    text("Score (%)", cBar, ry + 14);
    font(7.5, true);
    text("Benchmark (70%)", cBen - 14, ry + 14);
    font(8, true);
    text("Rating", cStar + 4, ry + 14);
    ry += hdrH;
    const rowH = 31;
    d.frameworks.forEach((f) => {
      const above = f.score >= 70;
      const main = above ? "#00A631" : "#FCAF1B";
      const soft = above ? "#AED7BB" : "#F8DA8C";
      // accent dot
      fill(f.bg || "#E6EDFA");
      pdf.circle(cF + 10, ry + rowH / 2, 10, "F");
      if (!icon("fw:" + f.label, cF + 3, ry + rowH / 2 - 7, 14, 14)) {
        fill(f.accent);
        pdf.circle(cF + 10, ry + rowH / 2, 3.2, "F");
      }
      const ls = wrap(f.label, 90, 8.2).slice(0, 2);
      font(8.2);
      ink(INK);
      const ty = ry + rowH / 2 - (ls.length - 1) * 5 + 3;
      ls.forEach((ln, i) => text(ln, cF + 26, ty + i * 10));
      // bar
      fill("#D9DDE7");
      pdf.roundedRect(cBar, ry + rowH / 2 - 3, 46, 6, 3, 3, "F");
      fill(main);
      const bw = (46 * Math.max(0, Math.min(100, f.score))) / 100;
      if (bw > 0) pdf.roundedRect(cBar, ry + rowH / 2 - 3, Math.max(bw, 3), 6, 3, 3, "F");
      font(8.5);
      ink(INK);
      text(`${f.score}%`, cBar + 51, ry + rowH / 2 + 3);
      // benchmark pill
      fill(above ? "#DDF3E4" : "#FFF0C7");
      pdf.roundedRect(cBen, ry + rowH / 2 - 8, 42, 16, 4, 4, "F");
      font(8, true);
      ink(above ? "#0B6B2A" : "#9A5B00");
      text(above ? "Above" : "Below", cBen + 21, ry + rowH / 2 + 3, { align: "center" });
      // stars
      const sr = 4.6;
      for (let i = 0; i < 5; i += 1) {
        const sx = cStar + 5 + i * 11;
        const sy = ry + rowH / 2 + 0.5;
        fill(soft);
        poly(star(sx, sy, sr), "F");
        const frac = Math.max(0, Math.min(1, (Math.min(100, f.score) - i * 20) / 20));
        if (frac > 0) {
          pdf.saveGraphicsState();
          pdf.rect(sx - sr - 1, sy - sr - 1, (sr * 2 + 2) * frac, sr * 2 + 2, null);
          pdf.clip();
          pdf.discardPath();
          fill(main);
          poly(star(sx, sy, sr), "F");
          pdf.restoreGraphicsState();
        }
      }
      stroke(GRID);
      pdf.setLineWidth(0.6);
      pdf.line(rx, ry + rowH, rx + rightW, ry + rowH);
      ry += rowH;
    });
    ry += 10;
    // banner
    const l1 = `${d.aboveCount} out of ${d.frameworks.length} framework are above the benchmark.`;
    const l2 = d.belowFrameworks.length ? `Focus on ${d.belowFrameworks.join(", ")} to improve overall Score.` : "";
    const l2w = l2 ? wrap(l2, rightW - 36, 8.5) : [];
    const bh = 18 + 11 + l2w.length * 11;
    fill("#EEF2FC");
    pdf.roundedRect(rx, ry, rightW, bh, 6, 6, "F");
    if (!icon("star", rx + 8, ry + bh / 2 - 9, 18, 18)) {
      fill("#7C3AED");
      poly(star(rx + 16, ry + bh / 2, 7), "F");
    }
    font(8.5, true);
    ink(NAVY);
    text(l1, rx + 32, ry + 15);
    font(8.5);
    l2w.forEach((ln, i) => text(ln, rx + 32, ry + 27 + i * 11));
    const rightEnd = ry + bh;

    y = Math.max(leftEnd, rightEnd) + GAP + 14;
  }

  /* ---------- Departments + Experience ---------- */
  const barChart = (x, w, title, rows, opts) => {
    let by = section(x, w, opts.heading, opts.iconKey);
    font(10.5, true);
    ink(NAVY);
    text(title, x, by + 2);
    by += 16;
    if (rows.length > 0 && rows.every((r) => !(Number(r.value) > 0))) {
      const eh = rows.length * 31 + 4 + 20;
      fill("#F6F8FD");
      pdf.roundedRect(x, by, w, eh, 8, 8, "F");
      font(9.5);
      ink(MUTED);
      text(opts.emptyMsg || "No data available", x + w / 2, by + eh / 2 + 3, { align: "center" });
      return by + eh;
    }
    const labelW = 96;
    const x0 = x + labelW + 6;
    const valueRoom = 34;
    const plotW = w - labelW - 6 - valueRoom;
    const rowH = 31;
    const plotH = rows.length * rowH + 4;
    // grid + ticks
    const ticks = opts.ticks;
    ticks.forEach((t) => {
      const gx = x0 + (plotW * t) / 100;
      stroke(GRID);
      pdf.setLineWidth(0.5);
      pdf.line(gx, by, gx, by + plotH);
      font(7.5);
      ink(INK);
      text(opts.tickFmt(t), gx, by + plotH + 11, { align: "center" });
    });
    stroke("#9AA3BC");
    pdf.setLineWidth(0.9);
    pdf.line(x0, by, x0, by + plotH);
    pdf.line(x0, by + plotH, x0 + plotW, by + plotH);
    const allZero = rows.length > 0 && rows.every((r) => !(Number(r.value) > 0));
    rows.forEach((r, i) => {
      const cyy = by + 2 + i * rowH + rowH / 2;
      const ls = wrap(r.label, labelW, 8).slice(0, 2);
      font(8);
      ink(INK);
      const ty = cyy - (ls.length - 1) * 4.5 + 2.8;
      ls.forEach((ln, k) => text(ln, x, ty + k * 9));
      if (allZero) return;
      const v = Math.max(0, Math.min(100, Number(r.value) || 0));
      const bw = (plotW * v) / 100;
      pillBar(x0, cyy - 4.5, bw, 9, r.color);
      font(8.5, true);
      ink("#000000");
      text(`${r.value}%`, x0 + bw + 6, cyy + 3);
    });
    if (allZero) {
      font(9);
      ink(MUTED);
      text(opts.emptyMsg || "No data available", x0 + plotW / 2, by + plotH / 2 + 3, { align: "center" });
    }
    by += plotH + 20;
    if (opts.insight) {
      const ins = wrap(opts.insight, w - 40, 8);
      const h = 14 + ins.length * 10.5;
      fill("#E3F4E8");
      pdf.roundedRect(x, by, w, h, 6, 6, "F");
      if (!icon(opts.noteKey, x + 7, by + h / 2 - 8, 16, 16)) {
        fill("#00A631");
        pdf.circle(x + 14, by + h / 2, 4, "F");
      }
      font(8);
      ink("#146B35");
      ins.forEach((ln, i) => text(ln, x + 26, by + 11 + i * 10.5));
      by += h;
    }
    return by;
  };
  {
    const colW = (CW - GAP) / 2;
    const rowsMax = Math.max(d.deptRows.length, d.expRows.length, 1);
    ensure(28 + 16 + rowsMax * 31 + 4 + 20 + 46);
    const top = y;
    const e1 = barChart(M, colW, d.deptTitle, d.deptRows, {
      heading: "Departments", iconKey: "headDepartments", noteKey: "noteDepartment", ticks: [0, 20, 40, 60, 80, 100], tickFmt: (t) => `${t}`, insight: d.deptInsight,
    });
    y = top;
    const e2 = barChart(M + colW + GAP, colW, "By Experience", d.expRows, {
      heading: "Experience", emptyMsg: "No experience data available", iconKey: "headExperience", noteKey: "noteExperience", ticks: [0, 25, 50, 75, 100], tickFmt: (t) => `${t}%`, insight: d.expInsight,
    });
    y = Math.max(e1, e2) + GAP + 14;
  }

  /* ================= PAGE 2 ================= */
  /* ---------- Gender comparison ---------- */
  if (d.gender) {
    const g = d.gender;
    const plotH = 190;
    ensure(plotH + 120);
    let gy = section(M, CW, "Gender Comparison", "headGender");
    const x0 = M + 30;
    const plotW = CW - 30 - 6;
    const top = gy + 14;
    [0, 20, 40, 60, 80, 100].forEach((t) => {
      const ty = top + plotH - (plotH * t) / 100;
      stroke(GRID);
      pdf.setLineWidth(0.5);
      pdf.setLineDashPattern([2, 2], 0);
      pdf.line(x0, ty, x0 + plotW, ty);
      pdf.setLineDashPattern([], 0);
      font(8);
      ink(INK);
      text(`${t}`, x0 - 8, ty + 2.8, { align: "right" });
    });
    stroke("#9AA3BC");
    pdf.setLineWidth(0.9);
    pdf.line(x0, top + plotH, x0 + plotW, top + plotH);
    const n = g.cats.length;
    const gw = plotW / n;
    const bw = Math.min(26, gw * 0.24);
    g.cats.forEach((cat, i) => {
      const gx = x0 + i * gw + gw / 2;
      [["#4352F3", g.male[i]], ["#959EFF", g.female[i]]].forEach(([col, v], k) => {
        const val = Math.max(0, Math.min(100, Number(v) || 0));
        const bh = (plotH * val) / 100;
        const bx = gx + (k === 0 ? -bw - 1.5 : 1.5);
        fill(col);
        if (bh > 3) {
          pdf.roundedRect(bx, top + plotH - bh, bw, bh, 3, 3, "F");
          pdf.rect(bx, top + plotH - Math.min(bh, 6), bw, Math.min(bh, 6), "F");
        } else if (bh > 0) {
          pdf.rect(bx, top + plotH - bh, bw, bh, "F");
        }
        font(8, true);
        ink("#000000");
        text(`${v ?? 0}%`, bx + bw / 2, top + plotH - bh - 4, { align: "center" });
      });
      const ls = wrap(cat, gw - 14, 8).slice(0, 3);
      font(8);
      ink(INK);
      ls.forEach((ln, k) => text(ln, gx, top + plotH + 14 + k * 10, { align: "center" }));
    });
    const ly = top + plotH + 14 + 3 * 10 + 8;
    const lx = M + CW / 2 - 50;
    fill("#4352F3");
    pdf.circle(lx, ly, 4, "F");
    font(9);
    ink(INK);
    text("Male", lx + 8, ly + 3);
    fill("#959EFF");
    pdf.circle(lx + 62, ly, 4, "F");
    text("Female", lx + 70, ly + 3);
    y = ly + 22 + GAP + 6;
  }

  /* ---------- Employee feedback summary ---------- */
  if (d.sentiment) {
    ensure(210);
    const sy0 = section(M, CW, "Employee Feedback Summary ( Open Ended )", "headFeedback");
    const s = d.sentiment;
    const total = s.positive + s.neutral + s.negative;
    const cx = M + 80;
    const ro = 70;
    const ri = 46;
    const cy = sy0 + ro + 6;
    let a = -90;
    [["#007221", s.positive], ["#FECD4C", s.neutral], ["#F24822", s.negative]].forEach(([col, v]) => {
      const sweep = total ? (360 * v) / total : 0;
      ringSeg(cx, cy, ro, ri, a, a + sweep, col);
      a += sweep;
    });
    font(10, true);
    ink(INK);
    text("Sentiment", cx, cy - 1, { align: "center" });
    text("Analysis", cx, cy + 11, { align: "center" });

    // legend under the donut
    let lg = cy + ro + 18;
    [["Positive", "#007221", s.positive], ["Neutral", "#FECD4C", s.neutral], ["Negative", "#F24822", s.negative]].forEach(
      ([lab, col, v]) => {
        fill(col);
        pdf.circle(M + 18, lg - 3, 4, "F");
        font(9);
        ink(INK);
        text(lab, M + 30, lg);
        text(`${v.toLocaleString()}(${total ? Math.round((v / total) * 100) : 0}%)`, M + 162, lg, { align: "right" });
        lg += 16;
      }
    );

    // sample feedback panel
    const px = M + 200;
    const pw = CW - 200;
    const items = d.feedback;
    let ph = 30;
    const wrapped = items.map((it) => {
      const w = wrap(it.text, pw - 62, 8.5);
      ph += Math.max(24, w.length * 11 + 8) + 14;
      return w;
    });
    ph += 8;
    const panelH = Math.max(ph, lg - sy0 + 4);
    fill("#F3F6FD");
    pdf.roundedRect(px, sy0 - 2, pw, panelH, 8, 8, "F");
    stroke("#E1E7F8");
    pdf.setLineWidth(0.7);
    pdf.roundedRect(px, sy0 - 2, pw, panelH, 8, 8, "S");
    font(11, true);
    ink(NAVY);
    text("Sample Feedback", px + 16, sy0 + 20);
    let fy = sy0 + 44;
    const itemHs = wrapped.map((w) => Math.max(24, w.length * 11 + 8));
    const availH = panelH - 44 - 20;
    const itemGap = items.length > 1
      ? Math.max(14, (availH - itemHs.reduce((a2, b) => a2 + b, 0)) / (items.length - 1))
      : 0;
    items.forEach((it, i) => {
      const col = it.type === "positive" ? "#00A631" : it.type === "neutral" ? "#F5B800" : "#E0342B";
      const fx = px + 26;
      // centre the emoji on the text block (works for 1 or 2+ lines)
      const fcy = fy - 1 + ((wrapped[i].length - 1) * 11) / 2;
      const emoKey = it.type === "positive" ? "emojiPositive" : it.type === "neutral" ? "emojiNeutral" : "emojiNegative";
      if (!icon(emoKey, fx - 9, fcy - 9, 18, 18)) {
        stroke(col);
        pdf.setLineWidth(1.2);
        pdf.circle(fx, fcy, 8, "S");
        fill(col);
        pdf.circle(fx - 3, fcy - 2.2, 0.9, "F");
        pdf.circle(fx + 3, fcy - 2.2, 0.9, "F");
        pdf.setLineWidth(1);
        if (it.type === "positive") {
          const m = arcPts(fx, fcy - 1.5, 4.6, 25, 155, 10);
          for (let k = 1; k < m.length; k += 1) pdf.line(m[k - 1][0], m[k - 1][1], m[k][0], m[k][1]);
        } else if (it.type === "negative") {
          const m = arcPts(fx, fcy + 6.5, 4.6, 205, 335, 10);
          for (let k = 1; k < m.length; k += 1) pdf.line(m[k - 1][0], m[k - 1][1], m[k][0], m[k][1]);
        } else {
          pdf.line(fx - 3.6, fcy + 3.5, fx + 3.6, fcy + 3.5);
        }
      }
      font(8.5);
      ink(INK);
      wrapped[i].forEach((ln, k) => text(ln, px + 46, fy + 1 + k * 11));
      fy += itemHs[i] + itemGap;
    });
    y = sy0 - 2 + panelH + GAP;
  }

  /* ---------- footers ---------- */
  const pages = pdf.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    pdf.setPage(p);
    stroke(HAIR);
    pdf.setLineWidth(0.7);
    pdf.line(M, PH - 26, PW - M, PH - 26);
    font(8);
    ink("#7A8396");
    text(d.companyName || "", M, PH - 14);
    text(`Page ${p} of ${pages}`, PW - M, PH - 14, { align: "right" });
  }
}

// Loads an image from /public as a PNG data URL for jsPDF (null if missing).
function loadIconData(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth || 64;
        c.height = img.naturalHeight || 64;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve(c.toDataURL("image/png"));
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/* ------------------------------------------------------------------
   StarRating — 5 stars, each a 20-point slice of the 100-point score,
   proportionally filled within its own slice.
   ------------------------------------------------------------------ */
function StarRating({ score, isAbove }) {
  const filledColor = isAbove ? "#00A631" : "#FCAF1B";
  const emptyColor = isAbove ? "#AED7BB" : "#F8DA8C";
  const clampedScore = Math.max(0, Math.min(100, score));

  return (
    <span className="analytics-rating-stars" aria-label={`${(clampedScore / 20).toFixed(1)} out of 5 stars`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const sliceStart = i * 20;
        const fillPercent = Math.max(0, Math.min(100, ((clampedScore - sliceStart) / 20) * 100));
        return (
          <span key={i} className="analytics-rating-star" style={{ color: emptyColor }}>
            ★
            <span
              className="analytics-rating-star-fill"
              style={{ width: `${fillPercent}%`, color: filledColor }}
              aria-hidden="true"
            >
              ★
            </span>
          </span>
        );
      })}
    </span>
  );
}

/* ------------------------------------------------------------------
   CardHead — the navy (#02133F) header bar every report card has:
   [icon box] [title].
   ------------------------------------------------------------------ */
function CardHead({ icon, title }) {
  return (
    <div className="analytics-card-head">
      <span className="analytics-card-head-icon">
        <img
          src={icon}
          alt=""
          onError={(e) => {
            // missing header image -> fall back to the demographic icon once
            if (!e.currentTarget.dataset.fb) {
              e.currentTarget.dataset.fb = "1";
              e.currentTarget.src = ICONS.headDemographic;
            }
          }}
        />
      </span>
      <span className="analytics-card-head-title">{title}</span>
    </div>
  );
}

/* ------------------------------------------------------------------
   ChartScroll — wraps a chart so it can scroll horizontally AND
   vertically when the screen is too small for it, with the scrollbars
   hidden. Mouse drag, swipe, Shift + wheel, or keyboard (Tab to the
   chart, then arrows / PageUp / PageDown).
   ------------------------------------------------------------------ */
function ChartScroll({ className = "", label = "Chart", children }) {
  const ref = useRef(null);
  const start = useRef({ x: 0, y: 0, left: 0, top: 0 });
  const [dragging, setDragging] = useState(false);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    // nothing to drag when the chart already fits
    if (el.scrollWidth <= el.clientWidth && el.scrollHeight <= el.clientHeight) return;
    start.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const el = ref.current;
      if (!el) return;
      el.scrollLeft = start.current.left - (e.clientX - start.current.x);
      el.scrollTop = start.current.top - (e.clientY - start.current.y);
    };
    const handleUp = () => setDragging(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [dragging]);

  return (
    <div
      ref={ref}
      className={`analytics-chart-scroll ${className}${dragging ? " is-dragging" : ""}`}
      tabIndex={0}
      role="group"
      aria-label={`${label} (scrollable)`}
      onMouseDown={handleMouseDown}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------
   Scroll / enter animations
   - useScrollReveal: watches every [data-reveal="id"] element and, the
     moment it scrolls into view, marks it data-in="1" (CSS reveals it) and
     reports { id: true } so charts can mount/draw only when seen.
   - useCountUp / CountUp: numbers that count up when revealed.
   All of it is skipped for people who prefer reduced motion.
------------------------------------------------------------------ */
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Canvas charts are mounted a moment AFTER their card has finished gliding in:
// Chart.js skips its grow-in animation if the card is still mid-transition.
const CHART_MOUNT_DELAY = { dept: 950, exp: 1110, gender: 950 };

function useScrollReveal(resetKey, hasReport) {
  const [seen, setSeen] = useState({});
  useEffect(() => {
    setSeen({});
    const timers = [];
    const els = Array.from(document.querySelectorAll("[data-reveal]"));
    els.forEach((el) => {
      delete el.dataset.in;
    });
    const markSeen = (id) => setSeen((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
    const revealNow = (el, instant) => {
      el.dataset.in = "1";
      const id = el.dataset.reveal;
      const delay = instant ? 0 : CHART_MOUNT_DELAY[id] || 0;
      if (delay) timers.push(setTimeout(() => markSeen(id), delay));
      else markSeen(id);
    };
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      els.forEach((el) => revealNow(el, true));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            revealNow(entry.target, false);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -5% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => {
      io.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [resetKey, hasReport]);
  return seen;
}

function useCountUp(target, run, duration = 1300, delay = 0) {
  const [val, setVal] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    const t = Number(target);
    if (!run || !isFinite(t)) {
      from.current = 0;
      setVal(0);
      return undefined;
    }
    if (prefersReducedMotion()) {
      from.current = t;
      setVal(t);
      return undefined;
    }
    const a = from.current;
    let raf = 0;
    let start = null;
    const timer = setTimeout(() => {
      const step = (ts) => {
        if (start === null) start = ts;
        const p = Math.min(1, (ts - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        const v = a + (t - a) * eased;
        from.current = v;
        setVal(v);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [target, run]);
  return Math.round(val);
}

function CountUp({ value, run, delay = 150, duration = 1200 }) {
  const n = typeof value === "number" ? value : Number(value);
  const numeric = typeof value === "number" || (typeof value === "string" && value.trim() !== "" && isFinite(n));
  const shown = useCountUp(numeric ? n : 0, run, duration, delay);
  return numeric ? shown.toLocaleString() : value;
}

function StatCard({ icon, iconBg, value, label, valueClassName, animate = true }) {
  return (
    <div className="analytics-stat-card">
      <span className="analytics-stat-icon" style={{ background: iconBg }}>
        <img src={icon} alt="" />
      </span>
      <div className="analytics-stat-text">
        <div className={`analytics-stat-value${valueClassName ? " " + valueClassName : ""}`}>
          <CountUp value={value} run={animate} />
        </div>
        <div className="analytics-stat-label">{label}</div>
      </div>
    </div>
  );
}

// Chart.js: bars grow in one after another, with a soft ease-out.
const GROW_MS = 1300;
const growDelay = (dataIndex, datasetIndex) => dataIndex * 110 + (datasetIndex || 0) * 70;
const GROW_ANIMATION = prefersReducedMotion()
  ? false
  : {
      duration: GROW_MS,
      easing: "easeOutQuart",
      delay: (ctx) =>
        ctx.type === "data" && ctx.mode === "default" ? growDelay(ctx.dataIndex, ctx.datasetIndex) : 0,
    };

// Chart.js drops its grow-in animation if the chart is resized right after it
// mounts (e.g. a scrollbar appearing). So: keep the canvas hidden for a beat
// while the layout settles, then replay the grow-in from zero and fade it in.
const replayGrow = (chart) => {
  if (!chart || chart.__replayed || prefersReducedMotion()) return;
  chart.__replayed = true;
  const canvas = chart.canvas;
  if (canvas) canvas.style.opacity = "0";
  setTimeout(() => {
    try {
      chart.reset();
      chart.$growT0 = performance.now();
      chart.update();
      if (canvas) {
        canvas.style.transition = "opacity 0.25s ease";
        canvas.style.opacity = "1";
      }
    } catch (e) {
      /* chart was unmounted */
    }
  }, 160);
};

// Bar-end value labels ("84%") count up from 0 in step with their bar.
// (Drawn by this small plugin because chartjs-plugin-datalabels caches its
// text and cannot change it frame by frame.)
const valueLabelsPlugin = {
  id: "valueLabels",
  afterDatasetsDraw(chart, _args, opts) {
    if (!opts) return;
    const { ctx } = chart;
    const t0 = chart.$growT0;
    ctx.save();
    ctx.font = opts.font;
    ctx.fillStyle = opts.color;
    chart.data.datasets.forEach((ds, di) => {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden) return;
      meta.data.forEach((el, i) => {
        const v = ds.data[i];
        if (v === null || v === undefined) return;
        let shown = v;
        if (t0 !== undefined) {
          const p = Math.max(0, Math.min(1, (performance.now() - t0 - growDelay(i, di)) / GROW_MS));
          shown = Math.round(v * (1 - Math.pow(1 - p, 4)));
        }
        const txt = shown + "%";
        if (opts.horizontal) {
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(txt, el.x + 6, el.y);
        } else {
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillText(txt, el.x, el.y - 5);
        }
      });
    });
    ctx.restore();
  },
};

/* ------------------------------------------------------------------
   ScoreGauge — 240° ring, open at the bottom (Figma): thick rounded
   progress arc over a light track, big % in the middle and the short
   certification label ("Certified") sitting in the gap at the bottom.
   ------------------------------------------------------------------ */
function ScoreGauge({ segments, score, scoreColor, label, labelTitle, animate = true }) {
  // arc runs from -120° to +120° (measured from 12 o'clock)
  const cx = 150;
  const cy = 140;
  const r = 100;
  const stroke = 28;
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const shownScore = useCountUp(pct, animate, 1500, 350);
  const fillColor = (segments && segments[0] && segments[0].color) || "#00A631";
  const trackColor = (segments && segments[1] && segments[1].color) || "#AED7BB";
  const rad = (deg) => (deg * Math.PI) / 180;
  const pt = (deg) => ({
    x: +(cx + r * Math.sin(rad(deg))).toFixed(2),
    y: +(cy - r * Math.cos(rad(deg))).toFixed(2),
  });
  const s = pt(-120);
  const e = pt(120);
  const arc = `M ${s.x} ${s.y} A ${r} ${r} 0 1 1 ${e.x} ${e.y}`;

  return (
    <div className="analytics-semi-gauge">
      <svg className="analytics-semi-gauge-svg" viewBox="0 0 300 232" role="img" aria-label={`${pct}%`}>
        <path d={arc} fill="none" stroke={trackColor} strokeWidth={stroke} strokeLinecap="round" />
        {pct > 0 ? (
          <path
            d={arc}
            fill="none"
            stroke={fillColor}
            strokeWidth={stroke}
            strokeLinecap="round"
            pathLength="100"
            className="analytics-semi-gauge-fill"
            strokeDasharray={`${animate ? pct : 0.001} 100`}
          />
        ) : (
          <circle cx={s.x} cy={s.y} r={stroke / 2} fill={fillColor} />
        )}
        <text x={cx} y="158" textAnchor="middle" fontSize="44" fontWeight="700" fill={scoreColor || fillColor}>
          {shownScore}%
        </text>
        {label && (
          <text x={cx} y="218" textAnchor="middle" fontSize={label.length > 9 ? 20 : 28} fontWeight="700" fill="#000000">
            <title>{labelTitle}</title>
            {label}
          </text>
        )}
      </svg>
    </div>
  );
}

// Fixed-width, left-aligned category-label column for the horizontal bar
// charts (Figma). Width = max(fraction of the chart, widest label + padding),
// so long labels are never clipped.
const fitLabelColumn = (fraction, fontSpec) => (scale) => {
  const ctx = scale.ctx;
  ctx.save();
  ctx.font = fontSpec;
  let widest = 0;
  scale.ticks.forEach((t) => {
    const lines = Array.isArray(t.label) ? t.label : [String(t.label)];
    lines.forEach((l) => {
      widest = Math.max(widest, ctx.measureText(l).width);
    });
  });
  ctx.restore();
  scale.width = Math.max(Math.round(scale.chart.width * fraction), Math.ceil(widest) + 18);
};

/* ------------------------------------------------------------------
   Sidebar — matches the Figma "Outstanding Workplaces" layout: dark
   navy panel, brand/logo at top, a section label, and nav items.
   Two destinations: Analytics and Employee Report. Clicking either
   just flips `activeView` in the parent — no route change.
   The Analytics item only appears when `showAnalytics` is true.
   ------------------------------------------------------------------ */

function Sidebar({ activeView, onNavigate, showAnalytics }) {
  const navItem = (key, label, dotColor, icon) => (
    <button
      type="button"
      onClick={() => onNavigate(key)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        borderRadius: 10,
        border: "none",
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        background: activeView === key ? "rgba(255,255,255,0.12)" : "transparent",
        color: "#fff",
        fontWeight: 600,
        fontSize: "0.95rem",
      }}
    >
      {icon ? (
        <img
          src={icon}
          alt=""
          style={{
            width: 18,
            height: 18,
            objectFit: "contain",
            flexShrink: 0,
          }}
        />
      ) : (
        <span
          style={{
            display: "inline-flex",
            width: 18,
            height: 18,
            borderRadius: 4,
            background: dotColor,
            flexShrink: 0,
          }}
        />
      )}
      {label}
    </button>
  );

  return (
    <aside
      style={{
        width: 260,
        minHeight: "100vh",
        background: "#0b1636",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        padding: "28px 20px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: 40 }}>
        <img
          src="/tm_cus.png"
          alt="Outstanding Workplaces"
          style={{
            width: "141px",
            height: "auto",
            display: "block",
            objectFit: "contain",
          }}
        />
      </div>

      <div
        style={{
          fontSize: "0.75rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: "#8b93b0",
          marginBottom: 14,
          textTransform: "uppercase",
        }}
      >
        Reports
      </div>

      <nav style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {navItem("employeeReport", "Employee Report", "#f5a623", "/cus_emp.png")}
        {showAnalytics && navItem("analytics", "Analytics", "#4f7cff", "/cus_ana.png")}
      </nav>
    </aside>
  );
}

export default function CustomerReportPage() {
  const navigate = useNavigate();
  const chartVars = useChartCSSVars();

  const [activeView, setActiveView] = useState(() => {
    try {
      return sessionStorage.getItem(ACTIVE_VIEW_KEY) === "analytics" ? "analytics" : "employeeReport";
    } catch {
      return "employeeReport";
    }
  });

  // ── Customer → company resolution ──
  const [companyId, setCompanyId] = useState(null);
  const [companyYear, setCompanyYear] = useState(null);
  const [yearRecords, setYearRecords] = useState([]);
  const [resolving, setResolving] = useState(true);
  const [resolveError, setResolveError] = useState("");

  const [yearFilter, setYearFilter] = useState(() => {
    try {
      return sessionStorage.getItem(YEAR_FILTER_KEY) || ALL_YEARS;
    } catch {
      return ALL_YEARS;
    }
  });

  // Save the selected year + tab so they survive a page refresh.
  useEffect(() => {
    try {
      sessionStorage.setItem(YEAR_FILTER_KEY, String(yearFilter));
      sessionStorage.setItem(ACTIVE_VIEW_KEY, activeView);
    } catch {}
  }, [yearFilter, activeView]);

  const [selfEvalScore, setSelfEvalScore] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDept, setSelectedDept] = useState("");
  const [selectedExperience, setSelectedExperience] = useState("");
  const [departmentList, setDepartmentList] = useState(FALLBACK_DEPARTMENTS);
  const [exporting, setExporting] = useState(false);
  const [hoveredSentiment, setHoveredSentiment] = useState(null);

  // Which sections have scrolled into view (drives the enter animations).
  const revealed = useScrollReveal(`${yearFilter}-${activeView}`, !!report);
  const sentimentDrawn = !!revealed.feedback;

  // [{ id, year, total, attended }] for every year, used ONLY to decide which
  // Analytics options are unlocked (60% response-rate rule).
  // null = not loaded yet (tab stays hidden until we know).
  const [yearStats, setYearStats] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("customerToken");
    if (!token) {
      navigate("/admin/customerlogin");
      return;
    }

    const payload = decodeJwtPayload(token);
    const loggedInUsername = payload?.username || "";

    if (!loggedInUsername) {
      setResolveError("Your session looks invalid. Please log in again.");
      setResolving(false);
      return;
    }

    axios
      .get(`${API_BASE_URL}/api/assessment/companies/list`)
      .then((res) => {
        const matches = (res.data || []).filter(
          (c) => (c.username || "").toLowerCase() === loggedInUsername.toLowerCase()
        );

        if (matches.length > 0) {
          const sorted = [...matches].sort((a, b) => (b.year || 0) - (a.year || 0));
          setYearRecords(sorted);
          setCompanyId(sorted[0].id);
          setCompanyYear(sorted[0].year || null);
        } else {
          setResolveError("No company is linked to this account yet. Please contact support.");
        }
        setResolving(false);
      })
      .catch(() => {
        setResolveError("Unable to load your report right now. Please try again later.");
        setResolving(false);
      });
  }, [navigate]);

  // Used by the Employee Report tab (it works on a single company/year).
  const handleYearSwitch = (record) => {
    setCompanyId(record.id);
    setCompanyYear(record.year || null);
    setSelectedDept("");
  };

  // Loads one year's report together with its self-evaluation score.
  const loadOneYear = async (rec) => {
    let evalScore = 0;
    try {
      const r = await axios.get(
        `${API_BASE_URL}/api/assessment/self-eval?company=${rec.id}&_=${Date.now()}`
      );
      evalScore = r.data.selfEvalScore || 0;
    } catch {}
    const res = await axios.get(
      `${API_BASE_URL}/api/assessment/report?company=${rec.id}&selfEval=${evalScore}&_=${Date.now()}`
    );
    return { ...res.data, __selfEval: evalScore, __year: rec.year };
  };

  // Loads total employees + completed count for EVERY year. Used only to
  // decide which Analytics options are unlocked. Refreshes every 10 seconds.
  useEffect(() => {
    if (!yearRecords.length) return;
    let cancelled = false;

    const loadStats = async () => {
      try {
        const rows = await Promise.all(
          yearRecords.map(async (rec) => {
            const res = await axios.get(
              `${API_BASE_URL}/api/assessment/report?company=${rec.id}&selfEval=0&_=${Date.now()}`
            );
            return {
              id: rec.id,
              year: rec.year,
              total: res.data.totalEmployees ?? res.data.employees_count ?? 0,
              attended: res.data.surveyAttended ?? 0,
            };
          })
        );
        if (!cancelled) setYearStats(rows);
      } catch {
        // keep the previous stats if a refresh fails
      }
    };

    loadStats();
    const timer = setInterval(loadStats, 10000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [yearRecords]);

  // Fetch data whenever the year filter changes.
  // "All Years" loads every year and merges them; a single year loads just that one.
  // Also silently re-fetches so admin changes (e.g. auditing score) show up automatically.
  useEffect(() => {
    if (!yearRecords.length) return;
    let cancelled = false;

    // silent = true -> background refresh: no spinner, keeps the user's selected department
    const load = async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const targets =
          yearFilter === ALL_YEARS
            ? yearRecords
            : yearRecords.filter((r) => String(r.id) === String(yearFilter));

        const reports = await Promise.all(targets.map(loadOneYear));
        if (cancelled) return;

        const finalReport = yearFilter === ALL_YEARS ? mergeReports(reports) : reports[0];
        setReport(finalReport);
        setSelfEvalScore(finalReport.__selfEval || 0);

        const deptKeys = finalReport?.departmentScores ? Object.keys(finalReport.departmentScores) : [];
        setDepartmentList(deptKeys.length ? deptKeys : FALLBACK_DEPARTMENTS);
        if (!silent) setSelectedDept("");
      } catch {
        if (!cancelled && !silent) {
          setReport(null);
          setDepartmentList(FALLBACK_DEPARTMENTS);
        }
      }
      if (!cancelled && !silent) setLoading(false);
    };

    load(false);

    const refresh = () => {
      if (document.visibilityState === "visible") load(true);
    };

    // 1) every 10 seconds  2) when the tab/window is focused again
    const timer = setInterval(refresh, 10000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);

    // 3) instant update when admin saves in the same browser (other tab)
    let channel = null;
    if ("BroadcastChannel" in window) {
      channel = new BroadcastChannel("self-eval-updates");
      channel.onmessage = () => load(true);
    }

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      if (channel) channel.close();
    };
    // eslint-disable-next-line
  }, [yearFilter, yearRecords]);

  const handleLogout = () => {
    localStorage.removeItem("customerToken");
    try {
      sessionStorage.removeItem(YEAR_FILTER_KEY);
      sessionStorage.removeItem(ACTIVE_VIEW_KEY);
      sessionStorage.removeItem("customer-employee-report-year");
    } catch {}
    navigate("/admin/customerlogin");
  };

  // ── Analytics access: completed / total employees >= 60% ──────────
  // Worked out per year and for All Years combined.
  const eligibleYearStats = (yearStats || []).filter(
    (s) => getResponseRate(s.attended, s.total) >= ANALYTICS_MIN_RESPONSE_RATE
  );
  const allTotal = (yearStats || []).reduce((sum, s) => sum + s.total, 0);
  const allAttended = (yearStats || []).reduce((sum, s) => sum + s.attended, 0);
  const allYearsEligible =
    yearStats !== null && getResponseRate(allAttended, allTotal) >= ANALYTICS_MIN_RESPONSE_RATE;

  // Tab shows if All Years OR at least one single year reaches 60%.
  const canSeeAnalytics = allYearsEligible || eligibleYearStats.length > 0;

  // Keep the selected year valid: it must be All Years or one of the real year records.
  useEffect(() => {
    if (yearFilter === ALL_YEARS || yearRecords.length === 0) return;
    const exists = yearRecords.some((r) => String(r.id) === String(yearFilter));
    if (!exists) setYearFilter(ALL_YEARS);
  }, [yearRecords, yearFilter]);

  useEffect(() => {
    // wait until the year stats have loaded before deciding
    if (yearStats !== null && !canSeeAnalytics && activeView === "analytics") {
      setActiveView("employeeReport");
    }
  }, [yearStats, canSeeAnalytics, activeView]);

  // ── Derived display values ────────────────────────────────────────
  const isSelfEvalEntered = selfEvalScore !== "" && !isNaN(Number(selfEvalScore)) && Number(selfEvalScore) > 0;
  const displayedScore = report ? (isSelfEvalEntered ? report.finalScore : report.surveyScore) : 0;
  const displayedCertification = report
    ? isSelfEvalEntered
      ? getCertificationCategory(report.finalScore)
      : getCertificationCategory(report.surveyScore)
    : "";
  const certificationShort = report
    ? getCertificationShortLabel(isSelfEvalEntered ? report.finalScore : report.surveyScore)
    : "—";

  const totalEmployees = report?.totalEmployees ?? report?.employees_count ?? 0;
  const totalResponses = report?.surveyAttended ?? 0;
  const totalUnResponses = report?.surveyUnattended ?? Math.max(totalEmployees - totalResponses, 0);

  const frameworkRows = (report?.sectionScores || []).map((s) => ({
    label: s.label,
    score: s.positive,
    style: getFrameworkStyle(s.label),
  }));
  const aboveCount = frameworkRows.filter((f) => f.score >= BENCHMARK).length;
  const belowFrameworks = frameworkRows.filter((f) => f.score < BENCHMARK).map((f) => f.label);

  /* Overall Score gauge: 2-tone ring — filled portion = displayedScore,
     track = light shade — coloured by whether the score is at/above the
     70% benchmark. */
  const isOverallAboveBenchmark = displayedScore >= BENCHMARK;
  const gaugeFilledColor = isOverallAboveBenchmark ? "#00A631" : "#FCAF1B";
  const gaugeEmptyColor = isOverallAboveBenchmark ? "#AED7BB" : "#F8DA8C";
  const gaugeSegments = [
    { value: displayedScore, color: gaugeFilledColor },
    { value: 100 - displayedScore, color: gaugeEmptyColor },
  ];

  const activeDeptSections = selectedDept
    ? report?.departmentScores?.[selectedDept] || []
    : report?.sectionScores || [];
  const deptBarLabels = activeDeptSections.map((s) => wrapChartLabel(s.label, 24));
  const deptBarData = activeDeptSections.map((s) => s.positive);

  // Lowest-performing department (only departments that have started).
  // A department counts as "started" if at least one topic has a
  // percentage above 0.
  const startedDepts = Object.entries(report?.departmentScores || {})
    .map(([name, sections]) => {
      const values = (sections || [])
        .map((s) => Number(s.positive))
        .filter((v) => !isNaN(v));
      return {
        name,
        isStarted: values.some((v) => v > 0),
        average: values.length
          ? Math.round(values.reduce((sum, v) => sum + v, 0) / values.length)
          : 0,
      };
    })
    .filter((d) => d.isStarted);

  // Need at least 2 started departments to have something to compare.
  const lowestAverage =
    startedDepts.length > 1 ? Math.min(...startedDepts.map((d) => d.average)) : null;

  // If several started departments tie for the lowest, all are shown.
  const lowestDepts =
    lowestAverage !== null ? startedDepts.filter((d) => d.average === lowestAverage) : [];

  // If every started department has the same score, there is no "lowest".
  const showLowestDept =
    lowestDepts.length > 0 && lowestDepts.length < startedDepts.length;

  const lowestDeptNames =
    lowestDepts.length > 1
      ? `${lowestDepts.slice(0, -1).map((d) => d.name).join(", ")} and ${
          lowestDepts[lowestDepts.length - 1].name
        }`
      : lowestDepts[0]?.name;

  // Shared axis styling for the two horizontal bar charts (Figma: a solid
  // left axis + bottom axis, no inner grid).
  const axisLine = {
    grid: { color: "transparent", drawTicks: false, drawBorder: true, borderColor: AXIS_LINE },
    border: { display: true, color: AXIS_LINE },
  };

  /* ---- Departments: horizontal coloured bars ---- */
  const departmentBarChartData = {
    labels: deptBarLabels.length ? deptBarLabels : [""],
    datasets: [
      {
        data: deptBarData,
        backgroundColor: deptBarData.map((_, i) => DEPT_BAR_COLORS[i % DEPT_BAR_COLORS.length]),
        borderRadius: { topLeft: 0, bottomLeft: 0, topRight: 20, bottomRight: 20 }, // flat at the axis, rounded (cylinder) end on the right
        borderSkipped: false,
        barThickness: 14,
      },
    ],
  };

  const departmentBarChartOptions = {
    indexAxis: "y",
    animation: GROW_ANIMATION,
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 44, top: 6, bottom: 4 } },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      valueLabels: { horizontal: true, color: "#000000", font: `700 12px ${CHART_FONT_FAMILY}` },
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        ...axisLine,
        ticks: { stepSize: 20, font: { family: CHART_FONT_FAMILY, size: 12 }, color: "#000000", padding: 10 },
      },
      y: {
        ...axisLine,
        // Figma: labels are left-aligned in a fixed column; the axis line starts after it
        afterFit: fitLabelColumn(0.27, `500 12px ${CHART_FONT_FAMILY}`),
        ticks: {
          font: { family: CHART_FONT_FAMILY, size: 12, weight: "500" },
          color: "#000000",
          padding: 12,
          crossAlign: "far", // "far" = left-aligned on a left axis
          autoSkip: false, // never hide a category label on short charts
        },
      },
    },
  };

  /* ---- Experience ---- */
  // Only real data. Groups with 0% (nobody answered) are hidden.
  // Display label: "Fresher (0-1 years)" -> "0-1 years".
  const experienceRows = (report?.experienceScores || [])
    .filter((r) => Number(r.value) > 0)
    .map((r) => ({ ...r, label: String(r.label).replace(/^\s*Fresher\s*\(([^)]*)\)\s*$/i, "$1") }));

  // "All Experience" dropdown: narrows the By Experience bars to one group.
  const experienceOptions = [
    { value: "", label: "All Experience" },
    ...experienceRows.map((r) => ({ value: r.label, label: r.label })),
  ];
  const activeExperience = experienceRows.some((r) => r.label === selectedExperience)
    ? selectedExperience
    : "";
  const visibleExperienceRows = activeExperience
    ? experienceRows.filter((r) => r.label === activeExperience)
    : experienceRows;

  // Highest-scoring experience group. If several groups tie for the top
  // score, all are listed. If every group has the same score, the banner hides.
  const validExperienceRows = experienceRows.filter((r) => !isNaN(Number(r.value)));

  const highestExpValue =
    validExperienceRows.length > 1
      ? Math.max(...validExperienceRows.map((r) => Number(r.value)))
      : null;

  const highestExpRows =
    highestExpValue !== null
      ? validExperienceRows.filter((r) => Number(r.value) === highestExpValue)
      : [];

  const showHighestExp =
    highestExpRows.length > 0 && highestExpRows.length < validExperienceRows.length;

  const highestExpLabels = highestExpRows.map((r) => r.label.toLowerCase());
  const highestExpNames =
    highestExpLabels.length > 1
      ? `${highestExpLabels.slice(0, -1).join(", ")} and ${highestExpLabels[highestExpLabels.length - 1]}`
      : highestExpLabels[0];

  const experienceChartData = {
    labels: visibleExperienceRows.map((r) => r.label),
    datasets: [
      {
        data: visibleExperienceRows.map((r) => r.value),
        backgroundColor: visibleExperienceRows.map((r) => r.color || "#4352f3"),
        borderRadius: { topLeft: 0, bottomLeft: 0, topRight: 20, bottomRight: 20 }, // flat at the axis, rounded (cylinder) end on the right
        borderSkipped: false,
        barThickness: 14,
      },
    ],
  };

  const experienceChartOptions = {
    indexAxis: "y",
    animation: GROW_ANIMATION,
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 44, top: 6, bottom: 4 } },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      valueLabels: { horizontal: true, color: "#000000", font: `700 12px ${CHART_FONT_FAMILY}` },
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        ...axisLine,
        ticks: {
          stepSize: 25,
          font: { family: CHART_FONT_FAMILY, size: 12 },
          color: "#000000",
          padding: 10,
          callback: (v) => v + "%",
        },
      },
      y: {
        ...axisLine,
        afterFit: fitLabelColumn(0.2, `500 13px ${CHART_FONT_FAMILY}`),
        ticks: {
          font: { family: CHART_FONT_FAMILY, size: 13, weight: "500" },
          color: "#000000",
          padding: 12,
          crossAlign: "far",
          autoSkip: false,
        },
      },
    },
  };

  /* ---- Gender comparison ---- */
  let genderChartData = null;
  let genderChartOptions = {};
  if (report && report.genderScores && report.genderScores.length > 0) {
    // Labels wrap onto at most two lines (e.g. "Employee Experience / & Engagement").
    const genderLabels = report.genderScores.map((s) => wrapChartLabel(s.label, 14));
    const malePositive = report.genderScores.map((s) => s.male?.positive ?? 0);
    const femalePositive = report.genderScores.map((s) => s.female?.positive ?? 0);

    genderChartData = {
      labels: genderLabels,
      datasets: [
        {
          label: "Male",
          data: malePositive,
          backgroundColor: "#4352f3",
          barPercentage: 0.9,
          categoryPercentage: 0.5,
          borderRadius: { topLeft: 4, topRight: 4 },
          borderSkipped: false,
        },
        {
          label: "Female",
          data: femalePositive,
          backgroundColor: "#959eff",
          barPercentage: 0.9,
          categoryPercentage: 0.5,
          borderRadius: { topLeft: 4, topRight: 4 },
          borderSkipped: false,
        },
      ],
    };

    // Fonts/sizes/colors come from the --gender-chart-* CSS variables.
    genderChartOptions = {
      animation: GROW_ANIMATION,
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 30, bottom: 6, left: 6, right: 0 } },
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          align: "center",
          labels: {
            boxWidth: 8,
            boxHeight: 8,
            font: {
              family: chartVars.chartFontFamily,
              size: chartVars.genderLegendFontSize,
              weight: chartVars.genderLegendFontWeight,
            },
            padding: 18,
            color: chartVars.genderLegendColor,
            pointStyle: "circle",
            usePointStyle: true,
          },
        },
        datalabels: { display: false },
        valueLabels: {
          horizontal: false,
          color: chartVars.genderDatalabelColor,
          font: `${chartVars.genderDatalabelFontWeight} ${chartVars.genderDatalabelFontSize}px ${chartVars.chartFontFamily}`,
        },
      },
      scales: {
        x: {
          offset: true,
          grid: {
            display: true,
            color: "#D9DCE6",
            borderDash: [2, 3],
            drawTicks: false,
            drawBorder: true,
            borderColor: "#B8BDCC",
          },
          border: { display: true, color: "#B8BDCC", dash: [2, 3] },
          ticks: {
            font: {
              family: chartVars.chartFontFamily,
              size: chartVars.genderChartXFontSize,
              weight: chartVars.genderChartXFontWeight,
            },
            color: chartVars.genderChartXColor,
            maxRotation: 0,
            minRotation: 0,
            autoSkip: false,
            padding: 12,
          },
        },
        y: {
          beginAtZero: true,
          max: 100,
          grid: { color: "#D9DCE6", borderDash: [2, 3], drawTicks: false, drawBorder: false },
          border: { display: false, dash: [2, 3] },
          ticks: {
            stepSize: 20,
            font: {
              family: chartVars.chartFontFamily,
              size: chartVars.genderChartYFontSize,
              weight: chartVars.genderChartYFontWeight,
            },
            color: chartVars.genderChartYColor,
            padding: 10,
          },
        },
      },
    };
  }

  /* ---- Sentiment donut + sample feedback ---- */
  // Real data only. All-zero counts => empty state.
  const sentiment =
    report?.sentimentAnalysis &&
    report.sentimentAnalysis.positive + report.sentimentAnalysis.neutral + report.sentimentAnalysis.negative > 0
      ? report.sentimentAnalysis
      : null;
  const sampleFeedback = report?.sampleFeedback?.length
    ? report.sampleFeedback
    : report
    ? MOCK_FEEDBACK
    : [];

  // Figma shows one sample per type (positive / neutral / negative).
  const sampleFeedbackShown = (() => {
    const onePerType = ["positive", "neutral", "negative"]
      .map((t) => sampleFeedback.find((f) => f.type === t))
      .filter(Boolean);
    return onePerType.length ? onePerType : sampleFeedback.slice(0, 3);
  })();

  const sentimentTotal = sentiment ? sentiment.positive + sentiment.neutral + sentiment.negative : 0;
  const sentimentPct = (n) => (sentimentTotal ? Math.round((n / sentimentTotal) * 100) : 0);
  const sentimentLevels = sentiment
    ? [
        { key: "positive", label: "Positive", count: sentiment.positive, color: "#007221" },
        { key: "neutral", label: "Neutral", count: sentiment.neutral, color: "#FECD4C" },
        { key: "negative", label: "Negative", count: sentiment.negative, color: "#F24822" },
      ].map((l) => ({ ...l, fraction: sentimentTotal ? l.count / sentimentTotal : 0 }))
    : [];
  const hoveredSentimentLevel = sentimentLevels.find((l) => l.key === hoveredSentiment) || null;
  const feedbackIcon = {
    positive: ICONS.emojiPositive,
    neutral: ICONS.emojiNeutral,
    negative: ICONS.emojiNegative,
  };

  const questionsTable = report?.questionsTable || [];
  const section70Totals = report?.section70Totals || {};
  const groupedQuestions = groupBySection(questionsTable);

  const departmentOptions = [
    { value: "", label: "All Departments" },
    ...departmentList.map((d) => ({ value: d, label: d })),
  ];

  // Key Insights: uses the same per-section "Total Score" shown in the
  // questions table (section70Totals). "Share Your Thoughts" is excluded
  // because it is open-ended and not scored.
  const scoredSections = Object.keys(groupedQuestions)
    .filter((section) => section !== "Share Your Thoughts")
    .map((section) => ({ section, score: Number(section70Totals[section]) }))
    .filter((s) => !isNaN(s.score));

  const strongestSection = scoredSections.length
    ? scoredSections.reduce((a, b) => (b.score > a.score ? b : a))
    : null;
  const weakestSection = scoredSections.length
    ? scoredSections.reduce((a, b) => (b.score < a.score ? b : a))
    : null;

  // If every section has the same score, the improvement line is hidden.
  const showWeakest =
    weakestSection && strongestSection && weakestSection.section !== strongestSection.section;

  // Label used in the report title: "(All Years)" or "(2026)" etc.
  const selectedYearRecord =
    yearFilter === ALL_YEARS ? null : yearRecords.find((r) => String(r.id) === String(yearFilter));
  const reportYearLabel =
    yearFilter === ALL_YEARS
      ? " (All Years)"
      : selectedYearRecord?.year
      ? ` (${selectedYearRecord.year})`
      : "";
  // Plain year text used in the downloaded files.
  const reportYearText =
    yearFilter === ALL_YEARS ? "All Years" : selectedYearRecord?.year ? String(selectedYearRecord.year) : "";

  // ── Export helpers ───────────────────────────────────────────────
  // Export PDF Report (top button): the whole report is drawn with vector
  // graphics on A4 pages. The Statements table is NOT included — it has its
  // own "Export To PDF" button.
  const handleExportChartsToPDF = async () => {
    if (!report || exporting) return;
    setExporting(true);
    try {
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

      // Real page icons -> data URLs (any that fail to load simply fall back
      // to a small drawn shape inside the PDF).
      const iconSources = {
        totalEmployees: ICONS.totalEmployees,
        totalResponses: ICONS.totalResponses,
        totalUnResponses: ICONS.totalUnResponses,
        medal: ICONS.medal,
        headOverall: ICONS.headOverall,
        headFramework: ICONS.headFramework,
        headDepartments: ICONS.headDepartments,
        headExperience: ICONS.headExperience,
        headGender: ICONS.headGender,
        headFeedback: ICONS.headFeedback,
        interpretationAbove: ICONS.interpretationAbove,
        interpretationBelow: ICONS.interpretationBelow,
        star: ICONS.star,
        noteDepartment: ICONS.noteDepartment,
        noteExperience: ICONS.noteExperience,
        emojiPositive: ICONS.emojiPositive,
        emojiNeutral: ICONS.emojiNeutral,
        emojiNegative: ICONS.emojiNegative,
      };
      frameworkRows.forEach((f) => {
        iconSources["fw:" + f.label] = f.style.icon;
      });
      const iconEntries = await Promise.all(
        Object.entries(iconSources).map(async ([k, src]) => [k, await loadIconData(src)])
      );
      const icons = {};
      iconEntries.forEach(([k, v]) => {
        if (v) icons[k] = v;
      });
      // header icons fall back to the demographic icon, like the page does
      const fallbackHead = await loadIconData(ICONS.headDemographic);
      ["headDepartments", "headExperience"].forEach((k) => {
        if (!icons[k] && fallbackHead) icons[k] = fallbackHead;
      });

      const frameworkAccent = {
        "Trust & Leadership": "#4BA3E3",
        "Employee Experience & Engagement": "#2DB34A",
        "Well-being & Inclusion": "#9B59D9",
        "Workplace Practices & Innovation": "#2563EB",
        "Sustainability & Social Impact": "#F2994A",
      };
      const interpretation = `Your overall score of ${displayedScore}% is ${
        isOverallAboveBenchmark ? "above" : "below"
      } the certification benchmark of ${BENCHMARK}%. ${
        isOverallAboveBenchmark
          ? "Keep up the great work and continue focusing on areas for improvement."
          : "Focus on the identified improvement areas to strengthen workplace practices."
      }`;

      drawAnalyticsPdf(pdf, {
        icons,
        companyName: report.companyName || "",
        year: reportYearText,
        generated: formatDownloadDate(),
        kpis: [
          { value: String(totalEmployees), label: "Total Employees", icon: "totalEmployees", color: "#F26B1D", tint: "#FDE9DC" },
          { value: String(totalResponses), label: "Total Responses", icon: "totalResponses", color: "#2656DC", tint: "#DCE7FD" },
          { value: String(totalUnResponses), label: "Total Un Responses", icon: "totalUnResponses", color: "#8B3FD9", tint: "#EBDCFD" },
          { value: String(certificationShort), label: "Certification Status", icon: "medal", color: "#00A631", tint: "#E3F8EA", valueColor: "#00A631", big: true },
        ],
        overall: {
          score: displayedScore,
          label: certificationShort,
          color: gaugeFilledColor,
          track: gaugeEmptyColor,
          survey: report.surveyScore,
          audit: isSelfEvalEntered ? selfEvalScore : 0,
          above: isOverallAboveBenchmark,
          interpretation,
        },
        frameworks: frameworkRows.map((f) => ({
          label: f.label,
          score: Number(f.score) || 0,
          accent: frameworkAccent[f.label] || "#2656DC",
          bg: f.style.bg,
        })),
        aboveCount,
        belowFrameworks,
        deptTitle: selectedDept || "All Departments",
        deptRows: activeDeptSections.map((sec, i) => ({
          label: sec.label,
          value: sec.positive,
          color: DEPT_BAR_COLORS[i % DEPT_BAR_COLORS.length],
        })),
        deptInsight: showLowestDept
          ? `With a score of ${lowestAverage}%, ${lowestDeptNames} represents the lowest-performing department in this assessment.`
          : "",
        expRows: visibleExperienceRows.map((r) => ({
          label: r.label,
          value: r.value,
          color: r.color || "#4352F3",
        })),
        expInsight: showHighestExp
          ? `Employees with ${highestExpNames} of experience demonstrated the highest satisfaction level, at ${highestExpValue}%.`
          : "",
        gender:
          report.genderScores && report.genderScores.length
            ? {
                cats: report.genderScores.map((sc) => sc.label),
                male: report.genderScores.map((sc) => sc.male?.positive ?? 0),
                female: report.genderScores.map((sc) => sc.female?.positive ?? 0),
              }
            : null,
        sentiment: sentiment
          ? { positive: sentiment.positive, neutral: sentiment.neutral, negative: sentiment.negative }
          : null,
        feedback: sampleFeedbackShown.map((f) => ({ type: f.type, text: f.text })),
      });

      const safeName = String(report.companyName || "company").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");
      pdf.save(`${safeName}-analytics-report.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);
      window.alert("Sorry, the PDF could not be generated. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  // Statements PDF (separate from the main report PDF): landscape A4, large
  // readable text, coloured Positive / Neutral / Negative headers, merged
  // Section + Total Score cells, zebra banding per section, header row repeated
  // on every page, Key Insights at the end and page numbers.
  const exportDeptTableToPDF = async (deptData, deptName) => {
    if (!deptData || !deptData.length) return;
    const keyInsightsIcon = await loadIconData(ICONS.keyInsights);
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const M = 30;
    const contentW = pageW - M * 2;
    let y = M;

    // title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(38, 86, 220);
    doc.text("Statements", M, y + 14);
    y += 24;
    const yr = reportYearText ? ` (${reportYearText})` : "";
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(0, 0, 0);
    doc.text(`${(report && report.companyName) || ""}${yr}  |  Generated ${formatDownloadDate()}`, M, y + 8);
    y += 22;

    const body = [];
    Object.entries(groupBySection(deptData))
      .filter(([section]) => section !== "Share Your Thoughts")
      .forEach(([section, questions], sIdx) => {
        const bg = sIdx % 2 === 0 ? [250, 250, 250] : [255, 255, 255];
        questions.forEach((q, idx) => {
          const remark = getRemarks(q.positive);
          const remarkFill = remark === "Strong" ? [0, 125, 58] : remark === "Good" ? [242, 133, 0] : [242, 72, 34];
          const row = [];
          if (idx === 0) {
            row.push({
              content: section,
              rowSpan: questions.length,
              styles: { halign: "center", valign: "middle", fontStyle: "bold", fontSize: 11, textColor: [11, 29, 81], fillColor: bg },
            });
          }
          row.push({ content: q.question, styles: { halign: "left", fillColor: bg } });
          row.push({ content: `${q.positive}%`, styles: { halign: "center", textColor: [11, 29, 81], fillColor: bg } });
          row.push({ content: `${q.neutral}%`, styles: { halign: "center", textColor: [11, 29, 81], fillColor: bg } });
          row.push({ content: `${q.negative}%`, styles: { halign: "center", textColor: [11, 29, 81], fillColor: bg } });
          row.push({
            content: remark,
            styles: { halign: "center", fontStyle: "bold", textColor: [255, 255, 255], fillColor: remarkFill },
          });
          if (idx === 0) {
            row.push({
              content: String(section70Totals[section] ?? ""),
              rowSpan: questions.length,
              styles: { halign: "center", valign: "middle", fontStyle: "bold", fontSize: 13, textColor: [0, 0, 0], fillColor: bg },
            });
          }
          body.push(row);
        });
      });

    autoTable(doc, {
      startY: y,
      head: [["Section", "Questions", "Positive", "Neutral", "Negative", "Remarks", "Total Score"]],
      body,
      theme: "plain",
      margin: { left: M, right: M, top: M, bottom: M + 16 },
      tableWidth: contentW,
      rowPageBreak: "avoid",
      styles: {
        fontSize: 10,
        cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
        lineColor: [225, 228, 236],
        lineWidth: 0.6,
        textColor: [0, 0, 0],
        valign: "middle",
        overflow: "linebreak",
      },
      headStyles: { fontStyle: "bold", fontSize: 11, halign: "center", valign: "middle" },
      columnStyles: {
        0: { cellWidth: 130 },
        2: { cellWidth: 66 },
        3: { cellWidth: 66 },
        4: { cellWidth: 66 },
        5: { cellWidth: 84 },
        6: { cellWidth: 78 },
      },
      didParseCell: (data) => {
        if (data.section !== "head") return;
        const col = data.column.index;
        if (col === 2) data.cell.styles.fillColor = [0, 125, 58];
        else if (col === 3) data.cell.styles.fillColor = [242, 133, 0];
        else if (col === 4) data.cell.styles.fillColor = [242, 72, 34];
        else data.cell.styles.fillColor = [237, 243, 253];
        data.cell.styles.textColor = col >= 2 && col <= 4 ? [255, 255, 255] : [0, 0, 0];
      },
    });
    y = (doc.lastAutoTable ? doc.lastAutoTable.finalY : y) + 16;

    // Key Insights
    if (strongestSection) {
      const items = [
        {
          label: "Strongest Area:",
          text: `"${strongestSection.section}" achieved the highest score of ${strongestSection.score}, reflecting positive employee perceptions in this area.`,
        },
      ];
      if (showWeakest) {
        items.push({
          label: "Area for Improvement:",
          text: `"${weakestSection.section}" recorded the lowest score of ${weakestSection.score}, highlighting an opportunity for further focus and improvement.`,
        });
      }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.5);
      const innerW = contentW - 32;
      const wrapped = items.map((it) => doc.splitTextToSize(`${it.label} ${it.text}`, innerW));
      const boxH = 40 + wrapped.reduce((sum, w) => sum + w.length * 14 + 8, 0);
      if (y + boxH > pageH - M - 16) {
        doc.addPage();
        y = M;
      }
      doc.setFillColor(244, 246, 253);
      doc.setDrawColor(215, 222, 240);
      doc.roundedRect(M, y, contentW, boxH, 6, 6, "FD");
      let ty = y + 24;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      let titleX = M + 16;
      if (keyInsightsIcon) {
        try {
          doc.addImage(keyInsightsIcon, "PNG", M + 16, ty - 14, 20, 20);
          titleX = M + 44;
        } catch (e) {
          /* icon unreadable: title only */
        }
      }
      doc.text("Key Insights", titleX, ty);
      ty += 22;
      items.forEach((it, i) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10.5);
        const labelW = doc.getTextWidth(`${it.label} `);
        const lines = wrapped[i];
        // first line: bold label + regular text; following lines regular
        doc.text(it.label, M + 16, ty);
        doc.setFont("helvetica", "normal");
        const rest = lines[0].slice(it.label.length + 1);
        doc.text(rest, M + 16 + labelW, ty);
        ty += 14;
        lines.slice(1).forEach((line) => {
          doc.text(line, M + 16, ty);
          ty += 14;
        });
        ty += 8;
      });
    }

    // page numbers
    const total = doc.getNumberOfPages();
    for (let p = 1; p <= total; p += 1) {
      doc.setPage(p);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(`Page ${p} of ${total}`, pageW / 2, pageH - 14, { align: "center" });
    }

    const safe = String((report && report.companyName) || deptName || "company")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-|-$/g, "");
    doc.save(`${safe}-statements.pdf`);
  };

  const exportDeptTableToExcel = async (deptData, deptName) => {
    if (!deptData || !deptData.length) return;
    const grouped = Object.entries(groupBySection(deptData)).filter(([section]) => section !== "Share Your Thoughts");
    const yr = reportYearText;
    const fileBase = String(deptName || "Overall").replace(/[^a-z0-9]+/gi, "_");
    try {
      // Browser bundle of exceljs (npm install exceljs)
      const ExcelJSModule = await import("exceljs/dist/exceljs.min.js");
      const ExcelJS = ExcelJSModule.default || ExcelJSModule;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Statements", {
        views: [{ showGridLines: false, state: "frozen", ySplit: 4 }],
        pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
      });
      const NAVY = "FF00123F";
      const thin = { style: "thin", color: { argb: "FFD7DEF0" } };
      const box = { top: thin, left: thin, bottom: thin, right: thin };
      ws.columns = [{ width: 30 }, { width: 70 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 14 }, { width: 13 }];

      ws.mergeCells("A1:G1");
      ws.getCell("A1").value = "Statements";
      ws.getCell("A1").font = { bold: true, size: 16, color: { argb: NAVY } };
      ws.mergeCells("A2:G2");
      ws.getCell("A2").value = `${(report && report.companyName) || ""}${yr ? ` (${yr})` : ""}${deptName && deptName !== "Overall" ? `  |  ${deptName}` : ""}  |  Generated ${formatDownloadDate()}`;
      ws.getCell("A2").font = { size: 10.5, color: { argb: "FF5B6577" } };
      ws.getRow(1).height = 24;

      // header row (row 4)
      const headers = ["Section", "Questions", "Positive", "Neutral", "Negative", "Remarks", "Total Score"];
      const headFill = (col) => ({
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: col === 2 ? "FF007D3A" : col === 3 ? "FFF28500" : col === 4 ? "FFF24822" : NAVY },
      });
      headers.forEach((t, i) => {
        const c = ws.getCell(4, i + 1);
        c.value = t;
        c.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
        c.fill = headFill(i);
        c.alignment = { horizontal: i === 1 ? "left" : "center", vertical: "middle" };
        c.border = box;
      });
      ws.getRow(4).height = 22;

      let r = 5;
      grouped.forEach(([section, questions], sIdx) => {
        const band = sIdx % 2 === 0 ? "FFF7F9FE" : "FFFFFFFF";
        const start = r;
        questions.forEach((q) => {
          const remark = getRemarks(q.positive);
          const remarkArgb = remark === "Strong" ? "FF007D3A" : remark === "Good" ? "FFF28500" : "FFF24822";
          const row = ws.getRow(r);
          row.getCell(2).value = q.question;
          row.getCell(3).value = Number(q.positive) / 100;
          row.getCell(4).value = Number(q.neutral) / 100;
          row.getCell(5).value = Number(q.negative) / 100;
          row.getCell(6).value = remark;
          for (let col = 1; col <= 7; col += 1) {
            const c = row.getCell(col);
            c.border = box;
            c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: band } };
            c.alignment = { vertical: "middle", horizontal: col === 2 ? "left" : "center", wrapText: col === 2, indent: col === 2 ? 1 : 0 };
          }
          [3, 4, 5].forEach((col) => { row.getCell(col).numFmt = "0%"; });
          row.getCell(6).font = { bold: true, color: { argb: "FFFFFFFF" } };
          row.getCell(6).fill = { type: "pattern", pattern: "solid", fgColor: { argb: remarkArgb } };
          row.height = 22;
          r += 1;
        });
        // merged Section + Total Score cells (like the page and the PDF)
        const first = questions[0] || {};
        const total = first.sectionTotalScore ?? first.totalScore ?? section70Totals[section] ?? "";
        const end = r - 1;
        if (end > start) {
          ws.mergeCells(start, 1, end, 1);
          ws.mergeCells(start, 7, end, 7);
        }
        const sc = ws.getCell(start, 1);
        sc.value = section;
        sc.font = { bold: true, size: 11, color: { argb: NAVY } };
        sc.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        const tc = ws.getCell(start, 7);
        tc.value = total === "" ? "" : Number(total);
        tc.font = { bold: true, size: 13 };
        tc.alignment = { vertical: "middle", horizontal: "center" };
        for (let rr = start; rr <= end; rr += 1) {
          [1, 7].forEach((col) => {
            const c = ws.getCell(rr, col);
            c.border = box;
            c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: band } };
          });
        }
      });

      // Key Insights block under the table
      if (strongestSection) {
        r += 1;
        const block = (text, bold) => {
          ws.mergeCells(r, 1, r, 7);
          const c = ws.getCell(r, 1);
          c.value = text;
          c.font = { bold, size: bold ? 12 : 10.5 };
          c.alignment = { vertical: "middle", wrapText: true };
          c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF4F6FD" } };
          ws.getRow(r).height = bold ? 22 : 30;
          r += 1;
        };
        block("Key Insights", true);
        block(`Strongest Area: "${strongestSection.section}" achieved the highest score of ${strongestSection.score}, reflecting positive employee perceptions in this area.`, false);
        if (showWeakest) {
          block(`Area for Improvement: "${weakestSection.section}" recorded the lowest score of ${weakestSection.score}, highlighting an opportunity for further focus and improvement.`, false);
        }
      }

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileBase}_report.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Styled Excel export failed, using plain export:", err);
      const rows = [];
      grouped.forEach(([section, questions]) => {
        questions.forEach((q, idx) => {
          rows.push({
            Section: idx === 0 ? section : "",
            Questions: q.question,
            Positive: q.positive + "%",
            Neutral: q.neutral + "%",
            Negative: q.negative + "%",
            Remarks: getRemarks(q.positive),
            "Total Score": idx === 0 ? q.sectionTotalScore ?? q.totalScore ?? section70Totals[section] ?? "" : "",
          });
        });
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [{ wch: 30 }, { wch: 70 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Statements");
      XLSX.writeFile(wb, `${fileBase}_report.xlsx`);
    }
  };

  // Company name shown in the top bar (falls back to "REPORTS").
  const customerCompanyName =
    (yearRecords.find((r) => String(r.id) === String(companyId)) || yearRecords[0])?.name ||
    "REPORTS";

  // ── Loading / error states (customer auth resolution) ─────────────
  if (resolving) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fafbfc" }}>
        <p style={{ color: "#666", fontSize: "1rem" }}>Loading your report...</p>
      </div>
    );
  }

  if (resolveError) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#fafbfc", gap: 16 }}>
        <p style={{ color: "#dc2626", fontSize: "1.05rem", maxWidth: 420, textAlign: "center" }}>{resolveError}</p>
        <button
          onClick={handleLogout}
          style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}
        >
          Back to Login
        </button>
      </div>
    );
  }

  const logoutButton = (
    <button
      onClick={handleLogout}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 20px",
        borderRadius: 8,
        border: "none",
        background: "#e11d2e",
        color: "#fff",
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="16 17 21 12 16 7" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="21" y1="12" x2="9" y2="12" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Logout
    </button>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar
        activeView={activeView}
        onNavigate={setActiveView}
        showAnalytics={canSeeAnalytics}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        {activeView === "employeeReport" ? (
          <main>
            {/* Top REPORTS bar */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px 40px",
                background: "#FFFFFF",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <h1 style={{ fontSize: "1.5rem", fontWeight: 800, margin: 0 }}>{customerCompanyName}</h1>
              {logoutButton}
            </div>

            {companyId ? (
              <EmployeeReports
                embedded
                lockedCompanyId={companyId}
                yearRecords={yearRecords}
                onYearChange={handleYearSwitch}
              />
            ) : (
              <div style={{ padding: 40, color: "#666" }}>Loading your employee report…</div>
            )}
          </main>
        ) : (
          <main className="analytics-page analytics-admin-report">
            {/* Top REPORTS bar */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px 40px",
                margin: "-23px -24px 20px",
                background: "#FFFFFF",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <h1 style={{ fontSize: "1.5rem", fontWeight: 800, color: "#0b1636", margin: 0 }}>
                {customerCompanyName}
              </h1>
              {logoutButton}
            </div>

            <div className="analytics-header">
              <h1 className="analytics-title">Analytics</h1>
              <p className="analytics-subtitle">Track and monitor all company and employees assessment progress</p>
            </div>

            <div className="analytics-toolbar">
              <FilterDropdown
                icon={ICONS.calendar}
                label="Year"
                placeholder="All Years"
                value={yearFilter}
                options={[
                  { value: ALL_YEARS, label: "All Years" },
                  ...yearRecords.map((r) => ({
                    value: String(r.id),
                    label: r.year ? String(r.year) : "—",
                  })),
                ]}
                onChange={(val) => setYearFilter(val)}
                minWidth={150}
              />

              <button
                type="button"
                className="analytics-export-pdf-btn"
                disabled={!report || exporting}
                onClick={handleExportChartsToPDF}
              >
                <img src={ICONS.uploadPdf} alt="" className="analytics-btn-icon" />
                {exporting ? "Preparing PDF…" : "Export PDF Report"}
              </button>
            </div>

            <div className="analytics-stats-grid" data-reveal="stats">
              <StatCard icon={ICONS.totalEmployees} iconBg="#FDE9DC" value={totalEmployees} label="Total Employees" animate={!!revealed.stats} />
              <StatCard icon={ICONS.totalResponses} iconBg="#DCE7FD" value={totalResponses} label="Total Responses" animate={!!revealed.stats} />
              <StatCard icon={ICONS.totalUnResponses} iconBg="#EBDCFD" value={totalUnResponses} label="Total Un Responses" animate={!!revealed.stats} />
              <StatCard
                icon={ICONS.medal}
                iconBg="#E3F8EA"
                value={certificationShort}
                label="Certification Status"
                valueClassName="analytics-stat-value-gold"
              />
            </div>

            {loading && !report && <div className="analytics-loading">Loading report…</div>}

            {report && (
              <div id="customer-analytics-export-root">
                {/* Company name sits in its own white card (see CSS) */}
                <div className="analytics-company-name">
                  {report.companyName} - Report{reportYearLabel}
                </div>

                {/* ---------- Overall Score (left) + Score By Framework (right) ---------- */}
                <div className="analytics-overview-row">
                  <div className="analytics-card analytics-overall-score-card" data-reveal="overall">
                    <CardHead icon={ICONS.headOverall} title="Overall Score" />
                    <div className="analytics-card-body">
                      <div className="analytics-score-stack">
                        <ScoreGauge
                          segments={gaugeSegments}
                          score={displayedScore}
                          scoreColor={gaugeFilledColor}
                          label={certificationShort}
                          labelTitle={displayedCertification}
                          animate={!!revealed.overall}
                        />

                        <div className="analytics-score-chips">
                          <div className="analytics-score-chip-row">
                            <span className="analytics-score-chip-label">70% Employee Survey</span>
                            <span className="analytics-score-chip-value">{report.surveyScore}</span>
                          </div>
                          <div className="analytics-score-chip-row">
                            <span className="analytics-score-chip-label">30% Auditing Evaluation</span>
                            <span className="analytics-score-chip-value">{selfEvalScore || 0}</span>
                          </div>
                        </div>

                        {/* "Interpretation" banner: green when the overall score
                            is at/above the 70% benchmark, amber when below. */}
                        <div
                          className={`analytics-interpretation-banner ${
                            isOverallAboveBenchmark ? "above" : "below"
                          }`}
                        >
                          <img
                            src={isOverallAboveBenchmark ? ICONS.interpretationAbove : ICONS.interpretationBelow}
                            alt=""
                            className="analytics-interpretation-icon"
                          />
                          <div>
                            <div className="analytics-interpretation-title">Interpretation</div>
                            <div className="analytics-interpretation-text">
                              Your overall score of {displayedScore}% is{" "}
                              {isOverallAboveBenchmark ? "above" : "below"} the certification benchmark of{" "}
                              {BENCHMARK}%.{" "}
                              {isOverallAboveBenchmark
                                ? "Keep up the great work and continue focusing on areas for improvement."
                                : "Focus on the identified improvement areas to strengthen workplace practices."}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="analytics-card analytics-framework-card" data-reveal="framework">
                    <CardHead icon={ICONS.headFramework} title="Score By Framework (Category-Wise)" />
                    <div className="analytics-card-body analytics-card-body-flush">
                      <ChartScroll className="analytics-framework-scroll" label="Score by framework table">
                        <table className="analytics-framework-table">
                          <thead>
                            <tr>
                              <th>Framework</th>
                              <th>Score (%)</th>
                              <th>Benchmark ({BENCHMARK}%)</th>
                              <th>Rating</th>
                            </tr>
                          </thead>
                          <tbody>
                            {frameworkRows.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="analytics-framework-empty">
                                  No framework data available.
                                </td>
                              </tr>
                            ) : (
                              frameworkRows.map((f, rowIdx) => (
                                <tr key={f.label}>
                                  <td className="analytics-framework-name-cell">
                                    <div className="analytics-framework-name-inner">
                                      <span className="analytics-framework-icon" style={{ background: f.style.bg }}>
                                        <img src={f.style.icon} alt="" />
                                      </span>
                                      <span className="analytics-framework-name-text">{f.label}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="analytics-framework-bar-row">
                                      <div className="analytics-framework-bar-track">
                                        <div
                                          className="analytics-framework-bar-fill"
                                          style={{
                                            width: `${f.score}%`,
                                            background: f.score >= BENCHMARK ? "#22c55e" : "#f59e0b",
                                          }}
                                        />
                                      </div>
                                      <span className="analytics-framework-score-text">
                                        <CountUp
                                          value={f.score}
                                          run={!!revealed.framework}
                                          delay={450 + rowIdx * 120}
                                          duration={1300}
                                        />
                                        %
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <span
                                      className={`analytics-framework-badge ${
                                        f.score >= BENCHMARK ? "above" : "below"
                                      }`}
                                    >
                                      {f.score >= BENCHMARK ? "Above" : "Below"}
                                    </span>
                                  </td>
                                  <td>
                                    <StarRating score={f.score} isAbove={f.score >= BENCHMARK} />
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </ChartScroll>

                      {frameworkRows.length > 0 && (
                        <div className="analytics-insight-banner">
                          <img src={ICONS.star} alt="" className="analytics-insight-icon" />
                          <span>
                            {aboveCount} out of {frameworkRows.length} framework are above the benchmark.
                            {belowFrameworks.length > 0 && (
                              <>
                                <br />
                                Focus on {belowFrameworks.join(", ")} to improve overall Score.
                              </>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ---------- Departments (left) + Experience (right) ---------- */}
                <div className="analytics-demo-row">
                  <div id="customer-analytics-dept-section" className="analytics-card analytics-demo-card" data-reveal="dept">
                    <CardHead icon={ICONS.headDepartments} title="Departments" />
                    <div className="analytics-card-body analytics-demo-body">
                      <div className="analytics-demo-toolbar">
                        <FilterDropdown
                          icon={ICONS.departmentFilter}
                          label="Department"
                          placeholder="All Departments"
                          value={selectedDept}
                          options={departmentOptions}
                          onChange={setSelectedDept}
                        />
                      </div>

                      <div className="analytics-demo-panel">
                        <div className="analytics-demo-panel-title">{selectedDept || "All Departments"}</div>
                        <ChartScroll className="analytics-demo-scroll" label="Department scores chart">
                          <div className="analytics-chart-box analytics-demo-chart-box">
                            {revealed.dept ? (
                              <Bar
                                ref={replayGrow}
                                data={departmentBarChartData}
                                options={departmentBarChartOptions}
                                plugins={[valueLabelsPlugin]}
                              />
                            ) : null}
                          </div>
                        </ChartScroll>
                      </div>

                      {showLowestDept && (
                        <div className="analytics-note-banner">
                          <img src={ICONS.noteDepartment} alt="" className="analytics-note-icon" />
                          <span>
                            With a score of {lowestAverage}%, {lowestDeptNames}{" "}
                            {lowestDepts.length > 1 ? "represent" : "represents"} the lowest-performing{" "}
                            {lowestDepts.length > 1 ? "departments" : "department"} in this assessment.
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div id="customer-analytics-exp-section" className="analytics-card analytics-demo-card" data-reveal="exp">
                    <CardHead icon={ICONS.headExperience} title="Experience" />
                    <div className="analytics-card-body analytics-demo-body">
                      <div className="analytics-demo-toolbar">
                        <FilterDropdown
                          icon={ICONS.experienceFilter}
                          label="Experience"
                          placeholder="All Experience"
                          value={activeExperience}
                          options={experienceOptions}
                          onChange={setSelectedExperience}
                        />
                      </div>

                      <div className="analytics-demo-panel">
                        <div className="analytics-demo-panel-title">By Experience</div>
                        {visibleExperienceRows.length === 0 ? (
                          <div className="analytics-empty-state">No experience data available.</div>
                        ) : (
                          <ChartScroll className="analytics-demo-scroll" label="By experience chart">
                            <div className="analytics-chart-box analytics-demo-chart-box">
                              {revealed.exp ? (
                                <Bar
                                  ref={replayGrow}
                                  data={experienceChartData}
                                  options={experienceChartOptions}
                                  plugins={[valueLabelsPlugin]}
                                />
                              ) : null}
                            </div>
                          </ChartScroll>
                        )}
                      </div>

                      {showHighestExp && (
                        <div className="analytics-note-banner">
                          <img src={ICONS.noteExperience} alt="" className="analytics-note-icon" />
                          <span>
                            Employees with {highestExpNames} of experience demonstrated the highest
                            satisfaction level, at {highestExpValue}%.
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ---------- Gender Comparison (left) + Feedback Summary (right) ---------- */}
                <div className="analytics-gender-feedback-row">
                  <div className="analytics-card analytics-gender-card" data-reveal="gender">
                    <CardHead icon={ICONS.headGender} title="Gender Comparison" />
                    <div className="analytics-card-body">
                      {genderChartData && genderChartData.labels && genderChartData.labels.length > 0 ? (
                        <ChartScroll className="analytics-gender-scroll" label="Gender comparison chart">
                          <div
                            className="analytics-chart-box analytics-gender-chart-box"
                            style={{ minWidth: genderChartData.labels.length * 150 }}
                          >
                            {revealed.gender ? (
                              <Bar
                                ref={replayGrow}
                                data={genderChartData}
                                options={genderChartOptions}
                                plugins={[valueLabelsPlugin]}
                              />
                            ) : null}
                          </div>
                        </ChartScroll>
                      ) : (
                        <div className="analytics-empty-state">No gender data available.</div>
                      )}
                    </div>
                  </div>

                  <div className="analytics-card analytics-feedback-outer" data-reveal="feedback">
                    <CardHead icon={ICONS.headFeedback} title="Employee Feedback Summary ( Open Ended )" />
                    <div className="analytics-card-body">
                      <div className="analytics-feedback-row">
                        <div className="analytics-sentiment-panel">
                          {!sentiment ? (
                            <div className="analytics-empty-state">No feedback data available.</div>
                          ) : (
                            <div className="analytics-sentiment-body">
                              <div className="analytics-sentiment-chart-wrap analytics-sentiment-animate">
                                <svg viewBox="0 0 200 200" className="analytics-sentiment-svg">
                                  {(() => {
                                    const r = 78, cx = 100, cy = 100, base = 34;
                                    const circ = 2 * Math.PI * r;
                                    let cumulative = 0;
                                    return sentimentLevels.map((lvl, i) => {
                                      const dash = (sentimentDrawn ? lvl.fraction : 0) * circ;
                                      const offset = cumulative * circ;
                                      cumulative += lvl.fraction;
                                      const isHovered = hoveredSentiment === lvl.key;
                                      return (
                                        <circle
                                          key={lvl.key}
                                          cx={cx}
                                          cy={cy}
                                          r={r}
                                          fill="none"
                                          stroke={lvl.color}
                                          strokeWidth={isHovered ? base + 6 : base}
                                          strokeDasharray={`${dash} ${circ - dash}`}
                                          strokeDashoffset={-offset}
                                          strokeLinecap="butt"
                                          transform={`rotate(-90 ${cx} ${cy})`}
                                          className="analytics-sentiment-segment"
                                          style={{
                                            opacity: hoveredSentiment && !isHovered ? 0.45 : 1,
                                            filter: isHovered ? "drop-shadow(0 0 4px rgba(0,0,0,0.25))" : "none",
                                            transitionDelay: sentimentDrawn ? `${i * 90}ms` : "0ms",
                                          }}
                                          onMouseEnter={() => setHoveredSentiment(lvl.key)}
                                          onMouseLeave={() => setHoveredSentiment(null)}
                                        />
                                      );
                                    });
                                  })()}
                                </svg>
                                <div className="analytics-sentiment-center">
                                  {hoveredSentimentLevel ? (
                                    <>
                                      <span className="analytics-sentiment-center-number">
                                        {hoveredSentimentLevel.count.toLocaleString()}
                                      </span>
                                      <span className="analytics-sentiment-center-text">
                                        {hoveredSentimentLevel.label}
                                      </span>
                                    </>
                                  ) : (
                                    "Sentiment Analysis"
                                  )}
                                </div>
                              </div>
                              <div className="analytics-sentiment-legend">
                                {sentimentLevels.map((lvl) => (
                                  <div
                                    key={lvl.key}
                                    className={`analytics-sentiment-legend-row${
                                      hoveredSentiment === lvl.key ? " analytics-sentiment-legend-active" : ""
                                    }`}
                                    onMouseEnter={() => setHoveredSentiment(lvl.key)}
                                    onMouseLeave={() => setHoveredSentiment(null)}
                                  >
                                    <span className="analytics-sentiment-dot" style={{ background: lvl.color }} />
                                    <span className="analytics-sentiment-legend-label">{lvl.label}</span>
                                    <span className="analytics-sentiment-legend-value">
                                      <CountUp value={lvl.count} run={sentimentDrawn} delay={500 + sentimentLevels.indexOf(lvl) * 120} duration={1400} />(
                                      <CountUp value={sentimentPct(lvl.count)} run={sentimentDrawn} delay={500 + sentimentLevels.indexOf(lvl) * 120} duration={1400} />%)
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="analytics-sample-feedback-panel">
                          <div className="analytics-sample-feedback-title">Sample Feedback</div>
                          {sampleFeedbackShown.length === 0 ? (
                            <div className="analytics-empty-state">No sample feedback available.</div>
                          ) : (
                            <ul className="analytics-feedback-list">
                              {sampleFeedbackShown.map((f, i) => (
                                <li key={i} className="analytics-feedback-item">
                                  <img
                                    src={feedbackIcon[f.type] || ICONS.emojiNeutral}
                                    alt=""
                                    className="analytics-feedback-emoji"
                                  />
                                  <span>{f.text}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="analytics-export-row">
                  <button
                    type="button"
                    className="analytics-export-btn analytics-export-excel"
                    disabled={!report || !questionsTable.length}
                    onClick={() => exportDeptTableToExcel(questionsTable, "Overall")}
                  >
                    <img src={ICONS.excel} alt="" className="analytics-btn-icon" />
                    Export To Excel
                  </button>
                  <button
                    type="button"
                    className="analytics-export-btn analytics-export-pdf"
                    disabled={!report || !questionsTable.length}
                    onClick={() => exportDeptTableToPDF(questionsTable, "Overall")}
                  >
                    <img src={ICONS.pdf} alt="" className="analytics-btn-icon" />
                    Export To PDF
                  </button>
                </div>

                {(questionsTable.length > 0 || strongestSection) && (
                  <div className="analytics-card analytics-statements-card">
                    <CardHead icon={ICONS.headStatements} title="Statements" />
                    {questionsTable.length > 0 && (
                      <div className="analytics-questions-table-wrap">
                        <table className="analytics-questions-table">
                          <thead>
                            <tr>
                              <th className="section">Section</th>
                              <th className="question">Questions</th>
                              <th className="positive">Positive</th>
                              <th className="neutral">Neutral</th>
                              <th className="negative">Negative</th>
                              <th className="remarks">Remarks</th>
                              <th className="score">Total Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(groupedQuestions)
                              .filter(([section]) => section !== "Share Your Thoughts")
                              .map(([section, questions], sectionIdx) => {
                                // Zebra-band the whole row per section:
                                // 1st/3rd/5th on #FAFAFA, 2nd/4th on #FFFFFF (CSS).
                                const rowShadeClass =
                                  sectionIdx % 2 === 0 ? "analytics-row-odd" : "analytics-row-even";
                                return questions.map((q, idx) => (
                                  <tr key={section + idx} className={rowShadeClass}>
                                    {idx === 0 && (
                                      <td rowSpan={questions.length} className="analytics-section-cell">
                                        {section}
                                      </td>
                                    )}
                                    <td className="analytics-question-cell" title={q.question}>
                                      {q.question}
                                    </td>
                                    <td className="positive">{q.positive}%</td>
                                    <td className="neutral">{q.neutral}%</td>
                                    <td className="negative">{q.negative}%</td>
                                    <td className="analytics-remarks-cell">
                                      <span className={`analytics-remarks-chip ${getRemarks(q.positive).toLowerCase()}`}>
                                        {getRemarks(q.positive)}
                                      </span>
                                    </td>
                                    {idx === 0 && (
                                      <td rowSpan={questions.length} className="analytics-score-cell">
                                        {section70Totals[section]}
                                      </td>
                                    )}
                                  </tr>
                                ));
                              })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Key Insights — sits under the Statements table */}
                    {strongestSection && (
                      <div className="analytics-key-insights">
                        <div className="analytics-key-insights-head">
                          <img src={ICONS.keyInsights} alt="" className="analytics-key-insights-icon" />
                          <span className="analytics-key-insights-title">Key Insights</span>
                        </div>
                        <p className="analytics-key-insights-line">
                          <strong>Strongest Area:</strong> "{strongestSection.section}" achieved the highest
                          score of {strongestSection.score}, reflecting positive employee perceptions in this area.
                        </p>
                        {showWeakest && (
                          <p className="analytics-key-insights-line">
                            <strong>Area for Improvement:</strong> "{weakestSection.section}" recorded the lowest
                            score of {weakestSection.score}, highlighting an opportunity for further focus and
                            improvement.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </main>
        )}
      </div>
    </div>
  );
}